/**
 * MatrixRTC — присутствие в звонке комнаты (MSC4143 / MSC3401).
 *
 * ЭТАП «плашка без медиа» (постановка Момо 10.09 17:40): самый дешёвый и
 * проверяемый кусок звонков. Мы показываем «в комнате идёт звонок · N
 * участников» по членству в MatrixRTC-сессии комнаты, НЕ подключая медиа.
 * Это видно без микрофона и без второго человека и сразу отвечает на
 * вопрос «жива ли связка клиент ↔ сервер ↔ RTC-события».
 *
 * ПОЧЕМУ MatrixRTCSessionManager, а не ручной разбор state-событий:
 * членство в звонке живёт в двух форматах сразу (современный m.rtc.member
 * и legacy m.call.member), плюс sticky-события MSC4354, плюс истечение
 * членств по таймеру. Переписывать это руками — значит завести второй,
 * гарантированно худший, движок членства рядом с SDK.
 *
 * E2EE в звонках НЕ включаем: crypto в этом клиенте выключен намеренно,
 * RTCEncryptionManager потянул бы то, чего у нас нет. Незашифрованный
 * звонок на стенде — осознанное решение (постановка).
 *
 * ЗАДАЧА 2026-09-14 (Антон): «в зашифрованной комнате нажатие Позвонить
 * не делает ничего. Ошибки нет». Причина: в E2EE-комнате MatrixRTC
 * пытается шифровать события звонка, но crypto в клиенте выключен.
 * joinRoomSession падает внутри SDK, а ошибка гасилась пустым catch
 * в MembershipManager — человек видел молчание. Теперь ошибка всплывает
 * в UI текстом, а кнопка в E2EE-комнате приглушена с подсказкой.
 */
import { debugLog, debugLogL } from "../debug";
import { tf, tr } from "../i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MatrixClient, MatrixEvent, Room } from "matrix-js-sdk";
// RoomEvent из matrix-js-sdk конфликтует по имени с livekit-client ниже —
// поэтому под псевдонимом. Один и тот же идентификатор для двух разных
// перечислений = очень дорогая в отладке опечатка.
import { RoomEvent as MatrixRoomEvent } from "matrix-js-sdk";
import { logger } from "matrix-js-sdk/lib/logger";
import {
  MatrixRTCSession,
  MatrixRTCSessionEvent,
  MatrixRTCSessionManager,
  MatrixRTCSessionManagerEvents,
} from "matrix-js-sdk/lib/matrixrtc/index.js";
import type { LivekitTransport } from "matrix-js-sdk/lib/matrixrtc/LivekitTransport.js";
import { markSelfLeftCall, clearSelfLeftOnRejoin } from "../notifications/selfLeftCalls";
import {
  clearStaleOwnCallMemberships,
  isForeignDeviceOwnMembership,
} from "./staleCallMembership";
import { stopRinging, startDialTone, stopDialTone } from "../notifications/callRing";
import { terminateActiveCall } from "../notifications/callTermination";
import { ensureFreshAccessToken } from "../auth/oidc";
import { config } from "../config";
import { isEncrypted } from "./rooms";
import { LocalAudioTrack, Room as LivekitRoom, RoomEvent, Track, supportsAudioOutputSelection } from "livekit-client";
import { CallDiagnostics, copyCallDiagnostics } from "./callDiagnostics";

/* ================ ВЫБОР УСТРОЙСТВ АУДИО/ВИДЕО (задача 2026-09-13) ================
 *
 * Постановка Антона 2026-09-13 14:16: «нужен выбор устройств аудио и видео».
 * Замер до постановки: enumerateDevices / switchActiveDevice / setSinkId
 * НЕ использовались нигде — треки брались сырым getUserMedia({audio:true}),
 * то есть всегда шло устройство по умолчанию. Это «не написано», а не
 * «сломано» — ниже функция дописана с нуля.
 *
 * ЧАСТИ:
 *  1. useMediaDevices — списки микрофонов/камер/динамиков через
 *     enumerateDevices + живое обновление по событию devicechange
 *     (воткнули наушники — список обновился сам, без перезахода).
 *  2. Persist выбора в localStorage (deviceId + label): сквозное
 *     требование Антона от 12.09 — выбранное устройство применяется
 *     к следующему звонку.
 *  3. Переключение НА ЛЕТУ: динамик — через room.switchActiveDevice;
 *     микрофон/камера — заменой MediaStreamTrack внутри существующей
 *     LiveKit LocalTrack. Публикация и SID остаются прежними, дубля нет.
 *  4. Исчезновение выбранного устройства (отключили гарнитуру) — возврат
 *     на системное по умолчанию и ТЕКСТ человеку, не молча (правило
 *     постановки п.3).
 *
 * ГОТЧА enumerateDevices (из постановки, учтена сразу): до выдачи
 * разрешения на микрофон/камеру браузер отдаёт устройства с ПУСТЫМИ
 * label. Поэтому список показывает «Устройство N» вместо пустой строки,
 * а осмысленные имена появляются после первого доступа к медиа.
 */

/** Одно устройство в списке выбора. */
export interface MediaDeviceItem {
  /** deviceId из enumerateDevices; "" — системное по умолчанию. */
  deviceId: string;
  /** Человекочитаемое имя; никогда не пустая строка (см. готчу выше). */
  label: string;
}

/** Три группы устройств для меню выбора. */
export interface MediaDeviceLists {
  mics: MediaDeviceItem[];
  cameras: MediaDeviceItem[];
  /** Пустой массив = setSinkId не поддерживается браузером (Safari) —
      группу динамиков в меню НЕ показываем вовсе (постановка п.2:
      «пункт не показывать вовсе, а не показывать неработающим»). */
  speakers: MediaDeviceItem[];
}

/** Ключи localStorage для persist выбора (deviceId + label). */
const LS_DEVICE_KEYS = {
  mic: "alg.call.device.mic",
  camera: "alg.call.device.camera",
  speaker: "alg.call.device.speaker",
} as const;

/** Слот выбора устройства: микрофон, камера или динамик. */
export type DeviceSlot = keyof typeof LS_DEVICE_KEYS;

/** Прочитать сохранённый выбор устройства. null — не сохранён или битый JSON. */
/**
 * Matrix user ID hidden inside a legacy LiveKit identity "@user:server:DEVICE".
 * Returns null for anonymised hashes and anything that is not a user ID.
 */
export function userIdFromLegacyIdentity(identity: string): string | null {
  const m = /^(@[^:]+:[^:]+)(?::.*)?$/.exec(identity);
  return m ? m[1] : null;
}

function readSavedDevice(slot: DeviceSlot): { deviceId: string; label: string } | null {
  try {
    const raw = localStorage.getItem(LS_DEVICE_KEYS[slot]);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { deviceId?: unknown; label?: unknown };
    if (typeof parsed.deviceId !== "string") return null;
    return { deviceId: parsed.deviceId, label: typeof parsed.label === "string" ? parsed.label : "" };
  } catch {
    return null;
  }
}

/** Сохранить выбор устройства (deviceId + label — label пригодится, чтобы
    показать «помним <имя>», даже если устройство сейчас отключено). */
function saveDevice(slot: DeviceSlot, deviceId: string, label: string): void {
  try {
    localStorage.setItem(LS_DEVICE_KEYS[slot], JSON.stringify({ deviceId, label }));
  } catch {
    // localStorage может быть недоступен (приватный режим) — выбор просто
    // не запомнится, звонок от этого не ломается.
  }
}

/* ================ ВЫБОР ДИНАМИКА НА WebKit (Антон 16.10 11:13) ================
 *
 * «Вывел экран на телевизор — наушники пропали насовсем, что ни
 * выбирай в меню». Причина в коде: supportsAudioOutputSelection() из
 * livekit-client 2.22.3 жёстко отвечает false для любого Safari-based
 * движка (utils.ts supportsSetSinkId: `if (isSafariBased()) return false`),
 * а Mac-приложение — это WKWebView, то есть WebKit. Поэтому в Mac-приложении
 * и в Safari группа «Динамик» в меню не показывалась ВООБЩЕ, звук всегда шёл
 * в системный выход по умолчанию, а macOS при подключении ТВ/AirPlay делает
 * умолчанием телевизор. Выбрать наушники внутри звонка было нечем.
 * WebKit поддерживает HTMLMediaElement.setSinkId с Safari 18.4 (MDN BCD) —
 * проверяем саму возможность, а не имя браузера, и на WebKit ставим sink
 * на наши <audio>-элементы сами, минуя LiveKit.
 */

/**
 * setSinkId есть у HTMLMediaElement (Chromium 49+, WebKit/Safari 18.4+).
 * iOS/iPadOS исключаем: там маршрут звука решает система (getUserMedia
 * перебивает выбор), и LiveKit сам не трогает устройства на iOS. Задача —
 * Mac (WKWebView) и десктопный Safari.
 */
function elementSinkSupported(): boolean {
  if (typeof HTMLMediaElement === "undefined" || !("setSinkId" in HTMLMediaElement.prototype)) return false;
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return false;
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return false; // iPadOS
  return true;
}

/** Выбор динамика возможен хоть каким-то путём. */
export function speakerSelectionSupported(): boolean {
  return supportsAudioOutputSelection() || elementSinkSupported();
}

/** Поставить sink одному элементу. "" = системный выход по умолчанию. */
function setElementSink(el: HTMLAudioElement, deviceId: string): Promise<void> {
  const sinkEl = el as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void>; sinkId?: string };
  const fn = sinkEl.setSinkId;
  if (typeof fn !== "function") return Promise.resolve();
  // Тот же выход — не дёргаем: лишняя смена sink на WebKit, по замечанию
  // LiveKit, может давать замедленный звук.
  if (sinkEl.sinkId === deviceId) return Promise.resolve();
  return fn.call(el, deviceId);
}

/**
 * Эхо (Антон 16.10 11:13): обработку звука микрофона задаём ЯВНО. Браузеры и так
 * включают её по умолчанию для getUserMedia, но явное значение (а) попадает
 * в getConstraints() трека, и LiveKit переносит его при каждом перезахвате
 * (смена устройства, ended, unmute — LocalTrack.restart берёт _constraints),
 * (б) делает факт проверяемым через getSettings() в диагностике звонка.
 */
const MIC_PROCESSING: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

/**
 * Список устройств по трём группам + подписка на devicechange.
 *
 * requestPermission=false: список строится БЕЗ запроса доступа — до
 * первого getUserMedia label будут пустыми, и мы покажем «Устройство N».
 * Как только человек вошёл в звонок (разрешение выдано), эффект
 * перечитает список и подставит настоящие имена — для этого у хука
 * есть параметр bump, который меняется после успешного getUserMedia.
 */
function useMediaDeviceLists(bump: number): MediaDeviceLists {
  const [lists, setLists] = useState<MediaDeviceLists>({ mics: [], cameras: [], speakers: [] });

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) return;
    let cancelled = false;

    const refresh = async () => {
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) return;
        const named = (kind: MediaDeviceKind, fallback: string): MediaDeviceItem[] => {
          const items = all.filter((d) => d.kind === kind);
          return items
            .map((d, i) => ({
              deviceId: d.deviceId,
              // ГОТЧА пустых label до разрешения: показываем «Устройство N»,
              // а не пустую строку (постановка). deviceId "default" браузер
              // даёт для системного умолчания — подписываем честно.
              label: d.label || (d.deviceId === "default" ? tr("call.deviceDefault") : `${fallback} ${i + 1}`),
            }))
            // Браузерный алиас "default" из списка УБИРАЕМ: у меню уже есть
            // явный первый пункт «По умолчанию» (deviceId ""), и два пункта
            // с одинаковой подписью путали бы человека. Логически это одно
            // и то же устройство.
            .filter((d) => d.deviceId !== "default");
        };
        setLists({
          mics: named("audioinput", tr("call.deviceMicrophone")),
          cameras: named("videoinput", tr("call.deviceCamera")),
          // Динамики только там, где setSinkId реально работает
          // (supportsAudioOutputSelection — штатная проверка livekit-client;
          // внутри это проверка setSinkId на HTMLMediaElement). На Safari
          // группа будет пустой, и меню её не покажет (постановка п.2).
          // 16.10: раньше здесь стоял только supportsAudioOutputSelection(),
          // который у LiveKit всегда false на WebKit — в Mac-приложении
          // выбора динамика не было вовсе (баг 1). Если WebKit не отдаёт
          // audiooutput в enumerateDevices, группа останется пустой и
          // меню её по-прежнему не покажет.
          speakers: speakerSelectionSupported() ? named("audiooutput", tr("call.deviceSpeaker")) : [],
        });
      } catch (e) {
        // enumerateDevices не должен падать, но если упал (политика,
        // повреждённый профиль) — оставляем прежний список, не молчим в лог.
        console.error("[call] enumerateDevices failed:", e);
      }
    };

    void refresh();
    // devicechange: воткнули/вытащили устройство — список обновился сам.
    navigator.mediaDevices.addEventListener("devicechange", refresh);
    return () => {
      cancelled = true;
      navigator.mediaDevices.removeEventListener("devicechange", refresh);
    };
  }, [bump]);

  return lists;
}


/**
 * Один менеджер на клиент. Создаётся лениво при первом открытии комнаты
 * со звонком: конструировать его на логин-экране было бы работой впустую —
 * до готовности sync всё равно нет комнат.
 *
 * Менеджер НИКОГДА не останавливается вместе с размонтированием отдельного
 * компонента: он обслуживает все комнаты клиента, и stop() на unmount одной
 * плашки погасил бы слежение за всеми остальными. Живёт, пока жив клиент.
 *
 * Экспортирован (шаг 3): глобальное отслеживание входящих звонков
 * (useIncomingCall в notifications/useIncomingCall.ts) обязано смотреть на
 * сессии ВСЕХ комнат через тот же единственный менеджер — второй менеджер
 * дал бы второй набор sticky-событий и второй таймер истечения членств.
 */
const managers = new WeakMap<MatrixClient, MatrixRTCSessionManager>();

export function managerFor(client: MatrixClient): MatrixRTCSessionManager {
  let m = managers.get(client);
  if (!m) {
    // Слот по умолчанию: application "m.call", id "ROOM" — то же, что
    // использует Element Call для комнатных звонков. Без слота менеджер
    // тоже работает (дефолт в SDK тот же), но лучше полагаться на
    // документированный дефолт конструктора: он и есть «основной звонок
    // комнаты».
    m = new MatrixRTCSessionManager(logger, client);
    m.start();
    managers.set(client, m);
  }
  return m;
}

export interface CallPresence {
  /** Идёт ли сейчас звонок в комнате (есть хотя бы одно живое членство). */
  active: boolean;
  /** Число участников звонка (уникальные членства, включая чужие устройства). */
  participants: number;
}

/**
 * Подписка на присутствие звонка в комнате.
 *
 * Пересчёт идёт по двум событиям менеджера (session_started/ended) И по
 * MembershipsChanged самой сессии: первые срабатывают на появление/исчезание
 * сессии целиком, второе — на вход/выход отдельных участников внутри неё.
 * Таймер истечения членств внутри MatrixRTCSession сам дёргает пересчёт,
 * поэтому «зависший» звонок без keep-alive гаснет сам, без нашего таймера.
 */
export function useCallPresence(client: MatrixClient, room: Room): CallPresence {
  const [presence, setPresence] = useState<CallPresence>({ active: false, participants: 0 });

  useEffect(() => {
    const manager = managerFor(client);
    const session: MatrixRTCSession = manager.getRoomSession(room);

    const recompute = () => {
      const myUserId = client.getUserId();
      const myDeviceId = client.getDeviceId();
      // ФАНТОМ СВОЕГО ЖЕ АККАУНТА НЕ ЕСТЬ ИДУЩИЙ ЗВОНОК.
      //
      // `isExpired()` для Session-членств смотрит только `expires` — то есть
      // календарь на 4 часа (`CallMembership.js:388`). Незакрытая вкладка
      // оставляет членство, и комната все эти часы выглядит «в звонке»:
      // именно это Антон видел 26.09 как «повесил трубку в Element X, а в
      // нашем клиенте звонок висит». Членство собственного userId с ДРУГОГО
      // устройства признаком звонка не считаем; своё текущее устройство —
      // считаем, оно и вправду в звонке.
      const memberships = session.memberships.filter(
        (m) =>
          !m.isExpired() &&
          !isForeignDeviceOwnMembership(m.userId, m.deviceId, myUserId, myDeviceId),
      );
      setPresence({
        active: memberships.length > 0,
        participants: memberships.length,
      });
    };

    recompute();

    const onSessionEvent = (roomId: string) => {
      if (roomId === room.roomId) recompute();
    };
    manager.on(MatrixRTCSessionManagerEvents.SessionStarted, onSessionEvent);
    manager.on(MatrixRTCSessionManagerEvents.SessionEnded, onSessionEvent);
    session.on(MatrixRTCSessionEvent.MembershipsChanged, recompute);

    // Протухание членств по настенным часам: SDK обновляет memberships
    // по своему таймеру, но расхождение в секунды даёт «плашка есть,
    // карточки нет». Пересчёт каждые 30 секунд закрывает это расхождение.
    // Антон, 2026-09-14: «звонок завершился, а плашка висит».
    const tick = setInterval(recompute, 30_000);

    return () => {
      clearInterval(tick);
      manager.off(MatrixRTCSessionManagerEvents.SessionStarted, onSessionEvent);
      manager.off(MatrixRTCSessionManagerEvents.SessionEnded, onSessionEvent);
      session.off(MatrixRTCSessionEvent.MembershipsChanged, recompute);
    };
  }, [client, room]);

  return presence;
}

/* ================= ШАГ 2: вход в звонок со звуком =================
 *
 * Постановка Момо 10.09 17:53 / 18:07. Расширяем шаг 1 (62ea1a2),
 * не переписываем: useCallPresence остаётся как был, добавляем useCallJoin.
 *
 * ПОЧЕМУ свой хук, а не расширение useCallPresence: присутствие — пассивная
 * подписка (нужна всегда, когда открыта комната), а вход — действие по
 * клику с побочками (микрофон, сеть, state-события). Смешать их в одном
 * хуке = тащить livekit-client в каждый рендер комнаты и рисковать
 * запросом микрофона без клика. Разделены намеренно.
 */

/** Фазы звонка. UI показывает их текстом, не молчит. */
export type CallJoinState = "idle" | "joining" | "in-call" | "error";

export interface CallJoin {
  /**
   * Комната зашифрована (E2EE). Звонки в таких комнатах сейчас не
   * поддерживаются (crypto выключен в клиенте). Используется для
   * приглушения кнопки звонка с подсказкой при наведении.
   */
  roomEncrypted: boolean;
  state: CallJoinState;
  /** Текст ошибки при state === "error". */
  error: string | null;
  /** Мы сейчас в звонке (опубликовали членство). */
  joined: boolean;
  /** Число живых членств в сессии, включая наше. */
  participants: number;
  /**
   * Войти: членство + токен + LiveKit + микрофон. Вызывать ТОЛЬКО по клику.
   * withVideo=true — ВИДЕОзвонок (STEP6, Антон 14:19 «просто, отдельной
   * кнопкой»): intent "video" + захват и публикация камеры. Единственный
   * вход в видео — кнопка 📹 в шапке; аудиокнопка 📞 вызывает с false
   * и НЕ затрагивает камеру вообще (аудиопуть не должен деградировать).
   */
  join: (withVideo?: boolean) => void;
  /** Выйти: снять членство И отключить LiveKit. */
  leave: () => void;
  /**
   * Секунд до автовыхода, когда я позвонил и никто не ответил
   * (null = отсчёта нет). Показывать обязательно: молча выкинутый из звонка
   * человек считает это сбоем, а не задуманным поведением.
   */
  soloSecondsLeft: number | null;
  /** Имя того, кто отклонил мой звонок (MSC4310), либо null. */
  declinedBy: string | null;
  /** Этот звонок начат как видео (кнопкой 📹). */
  isVideo: boolean;
  /** Наша камера включена (трек опубликован и не замьючен). */
  cameraOn: boolean;
  /**
   * Тумблер камеры. В аудиозвонке ТОЖЕ работает: первое включение
   * ЗАПРАШИВАЕТ камеру и публикует трек (Антон 12.09 09:21).
   */
  toggleCamera: () => void;
  /**
   * Камера сейчас запрашивается у браузера (ждём разрешения
   * или публикуем трек). Нужно, чтобы кнопка не выглядела
   * сломанной, пока висит системный запрос доступа.
   */
  cameraBusy: boolean;
  /**
   * Камера хоть раз была включена в этом звонке (трек существует).
   * Нужно интерфейсу, чтобы различить «выключил сам» (красная кнопка)
   * и «ещё не включал» (белая перечёркнутая) — Антон 12.09 10:26.
   */
  cameraInitialized: boolean;
  /**
   * Ошибка последней попытки включить камеру (отказ в доступе,
   * камера занята другим приложением и т.п.). Показать текстом,
   * а не глотать молча — иначе человек жмёт кнопку вхолостую.
   */
  cameraError: string | null;
  /** Наш микрофон включён. */
  micOn: boolean;
  /** Тумблер микрофона. */
  toggleMic: () => void;
  /**
   * ДЕМОНСТРАЦИЯ ЭКРАНА (Антон 12.09 12:28: «самый критический элемент»).
   * Идёт ли сейчас публикация экрана (трек жив и опубликован).
   */
  screenOn: boolean;
  /** Висит системный выбор окна/экрана — кнопку блокируем. */
  screenBusy: boolean;
  /**
   * Ошибка последней попытки (отказ в доступе, NotAllowedError).
   * Звонок при этом НЕ рвётся — правило постановки, показываем текстом.
   */
  screenError: string | null;
  /**
   * Включить/выключить демонстрацию экрана. Вызывать ТОЛЬКО кликом:
   * getDisplayMedia требует жеста пользователя. Остановка через
   * системную плашку браузера («Stop sharing») тоже корректно гасит
   * состояние — слушаем track.onended (типовой дефект «кнопка осталась
   * включенной» закрыт в реализации, см. ниже).
   */
  toggleScreenShare: () => void;
  /** Видеопотоки для раскладки: своё превью + чужие сеткой. */
  videoTiles: VideoTile[];
  /** Все реально подключённые к LiveKit люди, включая audio-only. */
  callParticipants: CallParticipant[];

  /* ---- Выбор устройств (задача 2026-09-13, Антон 14:16) ---- */
  /** Списки устройств по трём группам (см. MediaDeviceLists выше). */
  devices: MediaDeviceLists;
  /**
   * Активные deviceId сейчас. "" — системное по умолчанию (ничего не
   * выбрано или сохранённое устройство исчезло). В звонке синхронизируется
   * с LiveKit через RoomEvent.ActiveDeviceChanged.
   */
  activeDevices: { mic: string; camera: string; speaker: string };
  /**
   * Выбрать устройство. ВНЕ звонка — только запоминает (применится при
   * следующем join). В ЗВОНКЕ — переключает на лету через
   * room.switchActiveDevice БЕЗ разрыва звонка (постановка п.3).
   * kind: "mic" | "camera" | "speaker". deviceId "" = вернуться на
   * системное по умолчанию.
   */
  selectDevice: (kind: DeviceSlot, deviceId: string, label?: string) => void;
  /**
   * Текстовое уведомление про устройства (например, «выбранный микрофон
   * отключён — вернулись на устройство по умолчанию»). Показываем человеку,
   * не молчим (постановка п.3). null — уведомления нет.
   */
  deviceNotice: string | null;
  /** Сбросить уведомление (человек прочитал / закрыл). */
  dismissDeviceNotice: () => void;
  /** Есть приватно отфильтрованные данные текущего/последнего звонка. */
  diagnosticsAvailable: boolean;
  /** Скопировать ограниченный диагностический журнал в буфер обмена. */
  copyDiagnostics: () => Promise<void>;
}

/** Один видеоэлемент раскладки звонка (STEP6). */
export interface CallParticipant {
  identity: string;
  label: string;
  avatarMxc: string | null;
  local: boolean;
  speaking: boolean;
  /** Микрофон выключен: публикация аудио замьючена или её нет вовсе. */
  micMuted: boolean;
  /** Видеозвонок, а камера участника замьючена или не опубликована. */
  cameraOff?: boolean;
}

export interface VideoTile {
  /** Стабильный ключ: local — наше превью, identity — чужой участник. */
  key: string;
  /** Поток, который надо приаттачить к <video>. */
  stream: MediaStream;
  /** true — наше локальное превью: muted, маленькое. */
  local: boolean;
  /** Подпись (displayname чужого участника, «Вы» для своего). */
  label: string;
  /** LiveKit identity нужна только для привязки speaker events к плитке. */
  participantIdentity: string;
  /** LiveKit сейчас считает этого участника активным говорящим. */
  speaking: boolean;
  /** Микрофон участника выключен (заполняется при сборке CallJoin). */
  micMuted?: boolean;
  /**
   * Камера участника выключена (публикация камеры замьючена или снята).
   * Только для kind камеры; заполняется при сборке CallJoin. UI рисует
   * вместо чёрного видео плитку-заглушку (Антон 12.10).
   */
  cameraOff?: boolean;
  /**
   * КАМЕРА или ДЕМОНСТРАЦИЯ ЭКРАНА (screen share, Антон 12.09 12:28).
   * Различие обязательно: у экрана object-fit: contain (не обрезать
   * содержимое!), у камеры — cover; и у одного участника может быть
   * ДВЕ плитки одновременно (камера + экран), поэтому ключи плиток
   * кодируют источник ("cam:<identity>" / "screen:<identity>").
   * Опционально: старые вызовы (камера) поле не заполняют — undefined
   * читается как "camera", и обратной правки им не нужно.
   */
  kind?: "camera" | "screen";
}

/**
 * Адрес выдачи LiveKit-токенов. Замер Момо 10.09: POST с пустым телом
 * отвечает 400 «Missing room parameter» — сервис жив и ждёт JSON с room.
 * Контракт полей — как у matrix-livekit JWT-сервиса (livekit-auth / lk-jwt):
 * room, openid_token, device_id. Это тот же сервис, к которому ходит
 * Element Call; endpoint /sfu/get указывает на форк getify/lk-jwt-service,
 * у которого ровно этот путь и это тело.
 */
function livekitJwtUrl(): string {
  const homeserver = config.homeserverUrl;
  if (homeserver === "https://matrix.algsuccess.com") {
    return "https://livekit-jwt.algsuccess.com/sfu/get";
  }
  if (homeserver === "https://matrix1.algsuccess.com") {
    return "https://livekit-jwt1.algsuccess.com/sfu/get";
  }
  throw new Error(`Calls are not configured for homeserver ${new URL(homeserver).host}`);
}

/**
 * Official lk-jwt-service room alias for the default MatrixRTC call slot.
 * The service and Element X both use unpadded standard Base64(SHA-256(JSON
 * array [Matrix room ID, "m.call#ROOM"])). This is deliberately not a
 * sanitised room ID: using a home-grown alias puts interoperating clients in
 * different LiveKit rooms while Matrix signalling still appears healthy.
 */
async function livekitAliasFor(roomId: string): Promise<string> {
  const input = new TextEncoder().encode(JSON.stringify([roomId, "m.call#ROOM"]));
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", input));
  let binary = "";
  for (const byte of digest) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/=+$/, "");
}

/**
 * Compare the local LiveKit transport with transports advertised by the other
 * MatrixRTC memberships. Only aggregate match/difference counts leave this
 * function: aliases, service URLs, room IDs and identities are never logged.
 */
function addMatrixRtcTransportSnapshot(
  session: MatrixRTCSession,
  client: MatrixClient,
  localTransport: LivekitTransport,
  diagnostics: CallDiagnostics,
  detail: string,
): void {
  const alive = session.memberships.filter((membership) => !membership.isExpired());
  const oldest = alive[0];
  let remoteMemberships = 0;
  let remoteLivekitTransports = 0;
  let aliasMatches = 0;
  let aliasDiffers = 0;
  let aliasMissing = 0;
  let serviceMatches = 0;
  let serviceDiffers = 0;

  if (oldest) {
    for (const membership of alive) {
      if (
        membership.userId === client.getUserId() &&
        membership.deviceId === client.getDeviceId()
      ) continue;
      remoteMemberships += 1;
      const transport = membership.getTransport(oldest) as {
        type?: unknown;
        livekit_service_url?: unknown;
        livekit_alias?: unknown;
      } | undefined;
      if (!transport || transport.type !== "livekit") continue;
      remoteLivekitTransports += 1;
      if (transport.livekit_service_url === localTransport.livekit_service_url) serviceMatches += 1;
      else serviceDiffers += 1;
      if (typeof transport.livekit_alias !== "string") aliasMissing += 1;
      else if (transport.livekit_alias === localTransport.livekit_alias) aliasMatches += 1;
      else aliasDiffers += 1;
    }
  }

  diagnostics.add("matrixrtc-transport-snapshot", {
    detail,
    remoteMemberships,
    remoteLivekitTransports,
    aliasMatches,
    aliasDiffers,
    aliasMissing,
    serviceMatches,
    serviceDiffers,
  });
}

/**
 * Микрофон участника выключен по данным LiveKit: публикации микрофона нет
 * или она замьючена. Нет комнаты/участника — false (не рисуем ложный значок).
 */
function isMicMuted(lk: LivekitRoom | null, identity: string): boolean {
  if (!lk) return false;
  const participant =
    identity === lk.localParticipant.identity
      ? lk.localParticipant
      : lk.getParticipantByIdentity(identity);
  if (!participant) return false;
  const pub = participant.getTrackPublication(Track.Source.Microphone);
  return !pub || pub.isMuted;
}

/**
 * Камера участника выключена по данным LiveKit: публикации камеры нет
 * или она замьючена. Нет комнаты/участника — false (видео не прячем).
 */
function isCameraMuted(lk: LivekitRoom | null, identity: string): boolean {
  if (!lk) return false;
  const participant =
    identity === lk.localParticipant.identity
      ? lk.localParticipant
      : lk.getParticipantByIdentity(identity);
  if (!participant) return false;
  const pub = participant.getTrackPublication(Track.Source.Camera);
  return !pub || pub.isMuted;
}

/** Snapshot LiveKit's current state, including participants present at connect.
 * LiveKit does not emit ParticipantConnected/TrackPublished for pre-existing
 * participants, so event-only diagnostics cannot prove that the room was empty.
 */
function addLivekitRemoteSnapshot(
  lk: LivekitRoom,
  diagnostics: CallDiagnostics,
  detail: string,
): void {
  let remotePublications = 0;
  let subscribedPublications = 0;
  for (const participant of lk.remoteParticipants.values()) {
    remotePublications += participant.trackPublications.size;
    for (const publication of participant.trackPublications.values()) {
      if (publication.isSubscribed) subscribedPublications += 1;
    }
  }
  diagnostics.add("livekit-remote-snapshot", {
    detail,
    remoteParticipants: lk.remoteParticipants.size,
    remotePublications,
    subscribedPublications,
  });
}

/**
 * Вход в звонок: членство MatrixRTC + подключение LiveKit (только аудио).
 *
 * Порядок в join() важен и объяснен внутри:
 *  1. getUserMedia-подобный запрос микрофона (через livekit setMicrophoneEnabled)
 *     идёт ПЕРВЫМ делом после клика — пока жест пользователя «тёплый»,
 *     браузер покажет запрос разрешения. Если сначала ждать сеть, жест
 *     остынет, и Chrome может молча отклонить запрос.
 *  2. joinRoomSession публикует членство — другие видят нас.
 *  3. Токен + LiveKit connect.
 *
 * При ошибке на любом шаге — откат: leaveRoomSession + disconnect, чтобы
 * не остаться «в звонке» наполовину.
 */
export function useCallJoin(client: MatrixClient, room: Room, persistOnUnmount = false): CallJoin {
  const [state, setState] = useState<CallJoinState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState(0);
  // Версия нужна только для перерисовки кнопки после первой записи.
  // Сам журнал живёт в ref и никогда не попадает в Matrix/localStorage.
  const [, setDiagnosticsVersion] = useState(0);
  const diagnosticsRef = useRef<CallDiagnostics | null>(null);
  if (!diagnosticsRef.current) {
    diagnosticsRef.current = new CallDiagnostics(() => setDiagnosticsVersion((n) => n + 1));
  }
  const diagnostics = diagnosticsRef.current;
  const copyDiagnostics = useCallback(
    () => copyCallDiagnostics(diagnostics.text()),
    [diagnostics],
  );

  // Ресурсы, которые надо освободить при выходе/ошибке/смене комнаты.
  // В ref, а не в state: они не участвуют в рендере, и меняются вне цикла.
  const livekitRef = useRef<LivekitRoom | null>(null);
  const joinedRef = useRef(false);
  /**
   * One hidden <audio> element PER remote audio track, keyed by track sid.
   *
   * Root cause of Anton's 2026-09-17 07:03 call ("everyone heard me, I heard
   * nobody", 10 participants): all remote audio tracks were attached to a
   * single shared element. livekit-client's attach() removes any existing
   * track of the same kind from the element's MediaStream before adding the
   * new one, so only the LAST subscribed participant was ever audible — and
   * in that call the last one to subscribe was muted. Invisible in 1:1 calls,
   * guaranteed in groups.
   */
  const audioElsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  // Сырой микрофонный поток: держим отдельно от LiveKit, потому что берём его
  // ДО connect (см. join) и обязаны погасить сам, иначе индикатор записи в
  // браузере горит после выхода из звонка.
  const micStreamRef = useRef<MediaStream | null>(null);
  // Сырой поток КАМЕРЫ (STEP6): та же причина, что у микрофона — без
  // самостоятельного teardown индикатор камеры горит после звонка.
  const camStreamRef = useRef<MediaStream | null>(null);
  // Ссылка на опубликованный трек камеры — нужна тумблеру вкл/выкл
  // (mute/unmute), чтобы не перезапрашивать устройство.
  const camTrackRef = useRef<MediaStreamTrack | null>(null);
  // ДЕМОНСТРАЦИЯ ЭКРАНА (screen share, Антон 12.09 12:28).
  const screenStreamRef = useRef<MediaStream | null>(null);
  // Ref нужен путям, которые не ждут React-render (track.onended, ошибка
  // публикации, двойная остановка). State остаётся единственным источником
  // для UI: screenOn true только после успешной публикации.
  const screenTrackRef = useRef<MediaStreamTrack | null>(null);
  const [screenTrack, setScreenTrack] = useState<MediaStreamTrack | null>(null);
  const screenOn = screenTrack !== null;
  // Во время screen share исходящая камера временно выключена. Запоминаем
  // именно состояние ДО запуска и отдельно факт успешного выключения:
  // отмена системного picker не должна включить камеру, которая была off.
  const cameraWasOnBeforeScreenRef = useRef(false);
  const cameraDisabledForScreenRef = useRef(false);
  // Ждём системного выбора окна/экрана — второй клик в этот момент дал
  // бы второй getDisplayMedia и второй системный диалог.
  const screenBusyRef = useRef(false);
  const [screenBusy, setScreenBusy] = useState(false);
  const [screenError, setScreenError] = useState<string | null>(null);
  // Этот звонок — видео (кнопкой 📹). Храним в ref: join читает до setState.
  const isVideoRef = useRef(false);
  const [isVideo, setIsVideo] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  // Асинхронный screen-share переход должен видеть состояние камеры без
  // ожидания следующего render.
  const cameraOnRef = useRef(false);
  // Ждём разрешения/публикации камеры при добавлении видео в идущий
  // аудиозвонок. ref — чтобы второй клик не успел запустить второй
  // getUserMedia до перерисовки (state асинхронен, ref мгновенен).
  const cameraBusyRef = useRef(false);
  const [cameraBusy, setCameraBusy] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  // Камера была инициализирована в этом звонке (трек получен хотя бы раз).
  // Не совпадает с cameraOn: выключенная камера остаётся инициализированной.
  const [cameraInitialized, setCameraInitialized] = useState(false);
  const [micOn, setMicOn] = useState(true);
  // Раскладка видео: своя камера + чужие подписки (TrackSubscribed ниже).
  const [videoTiles, setVideoTiles] = useState<VideoTile[]>([]);
  // Источник — RoomEvent.ActiveSpeakersChanged от LiveKit. Храним одного
  // (первого, то есть самого громкого в упорядоченном списке LiveKit), чтобы
  // групповая сетка не получила несколько конкурирующих красных рамок.
  const [activeSpeakerIdentity, setActiveSpeakerIdentity] = useState<string | null>(null);
  // Отдельно от videoTiles: audio-only участник не публикует видео, но
  // всё равно обязан получить аватарную плитку и speaker-рамку.
  const [callParticipantIdentities, setCallParticipantIdentities] = useState<string[]>([]);
  // Индикатор выключенного микрофона (Антон 12.10): истина — в LiveKit
  // (TrackPublication.isMuted / наличие публикации). Счётчик лишь будит
  // ре-рендер на событиях mute/unmute/publish/unpublish; само значение
  // читается из livekitRef при сборке callParticipants/videoTiles.
  const [, setMicStateTick] = useState(0);
  const bumpMicState = useCallback(() => setMicStateTick((n) => n + 1), []);

  /* ---- ВЫБОР УСТРОЙСТВ (задача 2026-09-13) ----
   *
   * devicesPermissionBump: enumerateDevices до выдачи разрешения отдаёт
   * устройства с пустыми label (готча из постановки). После КАЖДОГО
   * успешного getUserMedia увеличиваем счётчик — хук списков перечитывает
   * устройства и подставляет настоящие имена.
   */
  const [devicesPermissionBump, setDevicesPermissionBump] = useState(0);
  const devices = useMediaDeviceLists(devicesPermissionBump);
  /**
   * Активные deviceId. "" = системное по умолчанию. Инициализируем из
   * localStorage (persist, Антон 12.09): сохранённый deviceId применяется
   * к следующему звонку; исчезновение отдельно отслеживает devicechange-
   * эффект ниже.
   */
  const [activeDevices, setActiveDevices] = useState<{ mic: string; camera: string; speaker: string }>(
    () => ({
      mic: readSavedDevice("mic")?.deviceId ?? "",
      camera: readSavedDevice("camera")?.deviceId ?? "",
      speaker: readSavedDevice("speaker")?.deviceId ?? "",
    }),
  );
  // ref-копия для колбэков, которым нужен актуальный выбор без пересоздания
  // (например, обработчик ActiveDeviceChanged живёт в join-колбэке).
  const activeDevicesRef = useRef(activeDevices);
  activeDevicesRef.current = activeDevices;
  // Уведомление человеку про устройства (исчез выбранный, не применился).
  const [deviceNotice, setDeviceNotice] = useState<string | null>(null);
  const dismissDeviceNotice = useCallback(() => setDeviceNotice(null), []);

  /**
   * Ограничение deviceId для getUserMedia. Выбранное устройство задаём
   * через exact: `ideal` разрешал браузеру молча выдать другое устройство,
   * а проверка по асинхронному списку devices пропускала сохранённый выбор,
   * если enumerateDevices ещё не успел завершиться к клику «Позвонить».
   *
   * Исчезнувшие устройства по-прежнему сбрасывает devicechange-эффект ниже.
   * Если устройство исчезло ровно между кликом и getUserMedia, браузер
   * вернёт нормальную ошибку вместо скрытой подмены источника.
   */
  const deviceConstraint = useCallback(
    (kind: "audio" | "video"): MediaTrackConstraints | undefined => {
      const id = kind === "audio" ? activeDevicesRef.current.mic : activeDevicesRef.current.camera;
      if (kind === "audio") return id ? { deviceId: { exact: id }, ...MIC_PROCESSING } : { ...MIC_PROCESSING };
      return id ? { deviceId: { exact: id } } : undefined;
    },
    [],
  );

  /**
   * Текущий sink наших <audio>-элементов на WebKit-пути ("" = умолчание).
   * На Chromium sink ведёт LiveKit (switchActiveDevice), там ref не нужен.
   */
  const elementSinkRef = useRef("");
  /** Выбранный динамик пропадал и мы временно играли в умолчание. */
  const speakerFallbackRef = useRef(false);

  /**
   * Применить динамик в идущем звонке. Chromium — штатный путь LiveKit
   * (он же ставит sink каждому новому треку). WebKit (Mac-приложение,
   * Safari 18.4+) — setSinkId прямо на наших элементах: LiveKit там
   * отказывается («the current browser does not support it»).
   */
  const applySpeaker = useCallback(
    async (lk: LivekitRoom, deviceId: string): Promise<boolean> => {
      if (supportsAudioOutputSelection()) {
        return lk.switchActiveDevice("audiooutput", deviceId === "" ? "default" : deviceId, false);
      }
      if (!elementSinkSupported()) throw new Error("AudioOutputSelectionUnsupported");
      elementSinkRef.current = deviceId;
      await Promise.all([...audioElsRef.current.values()].map((el) => setElementSink(el, deviceId)));
      return true;
    },
    [],
  );

  // Последний запрос на замену для каждого входа. Если человек быстро
  // выбрал A, затем B, более медленный getUserMedia(A) не должен победить B.
  const inputSwitchGenerationRef = useRef({ mic: 0, camera: 0 });
  const inputSwitchQueueRef = useRef<{ mic: Promise<void>; camera: Promise<void> }>({
    mic: Promise.resolve(),
    camera: Promise.resolve(),
  });

  /**
   * Выбрать устройство (п.3 постановки).
   *
   * Для всех трёх типов используем штатный switchActiveDevice. Для входов
   * принципиальны exact=true и последующая синхронизация наших raw refs/
   * превью с уже заменённой LiveKit LocalTrack. SID и публикация остаются
   * прежними — второй публикации и дубля на SFU не возникает.
   */
  const selectDevice = useCallback(
    (kind: DeviceSlot, deviceId: string, label?: string) => {
      const known =
        kind === "mic" ? devices.mics : kind === "camera" ? devices.cameras : devices.speakers;
      const resolvedLabel = label ?? known.find((d) => d.deviceId === deviceId)?.label ?? "";
      const commitSelection = () => {
        setActiveDevices((prev) => ({ ...prev, [kind]: deviceId }));
        saveDevice(kind, deviceId, resolvedLabel);
      };

      const lk = livekitRef.current;
      if (!lk) {
        commitSelection();
        diagnostics.add("device-selected", { kind, detail: "next-call" });
        return;
      }

      if (kind === "speaker") {
        void applySpeaker(lk, deviceId).then((ok) => {
          diagnostics.add("device-switch", {
            kind: "speaker",
            detail: `${ok ? "success" : "rejected"}.${supportsAudioOutputSelection() ? "livekit" : "element"}`,
          });
          if (ok) {
            commitSelection();
          } else {
            console.warn("[call] speaker switch returned false");
            setDeviceNotice(tr("ui.etot_brauzer_ne_dal_pereklyuchit_dinamik"));
          }
        }).catch((e) => {
          diagnostics.add("device-switch", { kind: "speaker", detail: "failure", error: e });
          console.error("[call] speaker switch failed");
          setDeviceNotice(
            tf("rtc.deviceSwitchError", {
              device: tr("ui.dinamik"),
              error: e instanceof Error ? e.message : String(e),
            }),
          );
        });
        return;
      }

      const source = kind === "mic" ? Track.Source.Microphone : Track.Source.Camera;
      const publication = lk.localParticipant.getTrackPublication(source);
      // Камера ещё не включалась (или join ещё публикует дорожки): запомним
      // выбор; следующий захват применит exact constraint.
      if (!publication?.track) {
        commitSelection();
        diagnostics.add("device-selected", { kind, detail: "next-capture" });
        return;
      }

      const generation = ++inputSwitchGenerationRef.current[kind];
      const localTrack = publication.track;
      const lkKind: MediaDeviceKind = kind === "mic" ? "audioinput" : "videoinput";
      const targetId = deviceId === "" ? "default" : deviceId;
      diagnostics.add("device-switch", { kind, detail: "replacement-start" });
      // Очередь исключает параллельные restartTrack на WebKit. Сам захват и
      // остановку старого входа оставляем LiveKit: его Safari-путь обязан
      // остановить старый track ДО getUserMedia, иначе новый может завершиться.
      inputSwitchQueueRef.current[kind] = inputSwitchQueueRef.current[kind].then(async () => {
        try {
          if (inputSwitchGenerationRef.current[kind] !== generation) return;
          const beforeReplacement = localTrack.mediaStreamTrack;
          const ok = await lk.switchActiveDevice(lkKind, targetId, true);
          if (!ok) throw new Error("DeviceReplacementRejected");
          if (livekitRef.current !== lk) return;

          const replacement = localTrack.mediaStreamTrack;
          if (kind === "mic") {
            micStreamRef.current = new MediaStream([replacement]);
          } else {
            camStreamRef.current = new MediaStream([replacement]);
            camTrackRef.current = replacement;
            // Превью обязано смотреть на новую дорожку, а не на остановленный
            // исходный MediaStreamTrack. Ключ плитки остаётся стабильным.
            setVideoTiles((prev) => prev.map((tile) =>
              tile.key === "cam:local"
                ? { ...tile, stream: new MediaStream([replacement]) }
                : tile,
            ));
          }

          commitSelection();
          setDevicesPermissionBump((n) => n + 1);
          // LiveKit откладывает restartTrack у muted-входа до unmute. Это
          // принятый выбор, но ещё не физическая замена; toggleCamera ниже
          // синхронизирует превью после фактического restart.
          diagnostics.add("device-switch", {
            kind,
            detail: replacement === beforeReplacement ? "replacement-deferred" : "replacement-success",
          });
        } catch (e) {
          diagnostics.add("device-switch", { kind, detail: "replacement-failure", error: e });
          // Не передаём Error в console: некоторые браузеры включают в
          // текст failed constraint. Диагностика выше сохраняет только
          // безопасные name/code/status, без deviceId и label.
          console.error(`[call] ${kind} replacement failed`);
          setDeviceNotice(
            tf("rtc.deviceSwitchError", {
              device: kind === "mic" ? tr("ui.mikrofon") : tr("ui.kameru"),
              error: e instanceof Error ? e.message : String(e),
            }),
          );
        }
      });
    },
    [devices.mics, devices.cameras, devices.speakers, diagnostics, applySpeaker],
  );

  /**
   * ВЫБРАННОЕ УСТРОЙСТВО ИСЧЕЗЛО (постановка п.3: «если выбранное
   * устройство исчезло — вернуться на системное по умолчанию и сказать
   * об этом человеку текстом, не молча»).
   *
   * Проверка идёт по событию devicechange (списки devices уже перечитаны
   * хуком выше): если активного deviceId больше нет в списке — сбрасываем
   * на "" (умолчание) и пишем уведомление. Проверяем только НЕПУСТЫЕ
   * активные id: пустой id — это уже умолчание, его «исчезать» нечему.
   *
   * LiveKit на devicechange сам дергает selectDefaultDevices (внутри SDK),
   * так что реальный трек переключится без нас; наша задача — привести
   * состояние/память в соответствие и СКАЗАТЬ человеку.
   */
  useEffect(() => {
    setActiveDevices((prev) => {
      const notices: string[] = [];
      const check = (
        slot: DeviceSlot,
        list: MediaDeviceItem[],
        what: string,
      ): string => {
        const id = prev[slot];
        if (!id) return id;
        if (list.length === 0) return id; // список ещё не загружен — не судим
        if (list.some((d) => d.deviceId === id)) return id;
        // Устройство исчезло. Восстановим его человекочитаемое имя из
        // localStorage (мы сохраняли label как раз для этого случая).
        const remembered = readSavedDevice(slot)?.label;
        notices.push(
          tf("rtc.deviceDisconnectedFallback", {
            device: what,
            name: remembered || tr("ui.vybrannoe_ranee"),
          }),
        );
        saveDevice(slot, "", "");
        return "";
      };
      const mic = check("mic", devices.mics, tr("call.deviceMicrophone"));
      const camera = check("camera", devices.cameras, tr("call.deviceCamera"));
      // Динамик здесь НЕ трогаем — у него свой эффект ниже (16.10):
      // сохранённый выбор при исчезновении больше не стирается, чтобы
      // вернуть наушники, когда они снова появятся.
      const speaker = prev.speaker;
      if (mic === prev.mic && camera === prev.camera && speaker === prev.speaker) return prev;
      if (notices.length > 0) setDeviceNotice(notices.join(" "));
      return { mic, camera, speaker };
    });
  }, [devices]);

  /**
   * ДИНАМИК НА devicechange (баг 1, Антон 16.10).
   *
   * Раньше исчезновение выбранного динамика (на подключении ТВ/AirPlay
   * macOS пересобирает аудиомаршруты, Bluetooth-наушники могут
   * кратковременно пропасть из списка) СТИРАЛО выбор в localStorage
   * (saveDevice(slot, "", "")) — и когда наушники возвращались, звонок
   * оставался на «умолчании», то есть на телевизоре. Теперь:
   *  • выбор человека (localStorage) — это намерение, оно живёт, пока
   *    человек сам не выберет другое;
   *  • выбранного нет в списке — временно играем в умолчание и говорим об
   *    этом текстом;
   *  • выбранный снова в списке — возвращаем звук на него и говорим;
   *  • на каждом devicechange в звонке заново ставим sink (маршрут мог
   *    смениться под нами).
   */
  useEffect(() => {
    if (devices.speakers.length === 0) return;
    let saved = readSavedDevice("speaker");
    const present = (id: string) => devices.speakers.some((d) => d.deviceId === id);
    // deviceId — соль на origin; после очистки данных сайта/переустановки
    // приложения id тех же наушников может смениться. Тогда узнаём их по
    // сохранённому имени и обновляем запись, а не теряем выбор.
    if (saved?.deviceId && !present(saved.deviceId) && saved.label) {
      const byLabel = devices.speakers.filter((d) => d.label === saved!.label);
      if (byLabel.length === 1) {
        saved = { deviceId: byLabel[0].deviceId, label: saved.label };
        saveDevice("speaker", saved.deviceId, saved.label);
        diagnostics.add("device-switch", { kind: "speaker", detail: "preferred-relinked-by-label" });
      }
    }
    const preferred = saved?.deviceId ?? "";
    const active = activeDevicesRef.current.speaker;
    const lk = livekitRef.current;

    if (preferred && !present(preferred)) {
      speakerFallbackRef.current = true;
      if (active === "") return;
      setActiveDevices((prev) => ({ ...prev, speaker: "" }));
      setDeviceNotice(
        tf("rtc.deviceDisconnectedFallback", {
          device: tr("call.deviceSpeaker"),
          name: saved?.label || tr("ui.vybrannoe_ranee"),
        }),
      );
      if (lk) {
        void applySpeaker(lk, "").catch((e) =>
          diagnostics.add("device-switch", { kind: "speaker", detail: "fallback-failure", error: e }),
        );
      }
      diagnostics.add("device-switch", { kind: "speaker", detail: "preferred-missing" });
      return;
    }

    if (!lk) {
      if (active !== preferred) setActiveDevices((prev) => ({ ...prev, speaker: preferred }));
      return;
    }
    const restoring = active !== preferred;
    const wasFallback = speakerFallbackRef.current;
    speakerFallbackRef.current = false;
    // Ничего не выбрано и ничего не меняется — sink не трогаем: LiveKit
    // отмечает, что лишняя смена выхода на Safari замедляет звук.
    if (!restoring && preferred === "") return;
    void applySpeaker(lk, preferred).then((ok) => {
      if (livekitRef.current !== lk || !ok) return;
      setActiveDevices((prev) => (prev.speaker === preferred ? prev : { ...prev, speaker: preferred }));
      if (restoring && preferred && wasFallback) {
        setDeviceNotice(tf("rtc.speakerRestored", { name: saved?.label || tr("ui.vybrannoe_ranee") }));
      }
      diagnostics.add("device-switch", { kind: "speaker", detail: restoring ? "preferred-restored" : "sink-reapplied" });
    }).catch((e) => {
      diagnostics.add("device-switch", { kind: "speaker", detail: "reapply-failure", error: e });
    });
  }, [devices, applySpeaker, diagnostics]);

  const manager = managerFor(client);
  // Мемоизируем сессию: getRoomSession НЕ мемоизирован и при каждом рендере
  // мог бы вернуть новый объект (хотя в текущем SDK возвращает тот же).
  // Если бы session менялась, useCallback(teardown, [session]) пересоздавался
  // бы, эффект cleanup размонтирования срабатывал на каждый рендер, и
  // leaveRoomSession вызывался бы при КАЖДОМ перерендере — человек входил
  // в звонок и тут же выходил. Мемоизация страхует от этого класса бага.
  const session: MatrixRTCSession = useMemo(() => manager.getRoomSession(room), [manager, room]);

  // СТРАХОВКА от teardown по перерендеру (дефект «звонки в комнату не
  // работают», Антон 12:54): если room не сменился, а эффект cleanup всё
  // равно срабатывает — значит, teardown пересоздался по session. Лог
  // покажет это в консоли.
  const prevRoomId = useRef(room.roomId);
  useEffect(() => {
    if (prevRoomId.current !== room.roomId) {
      debugLogL(`[call] room changed: ${prevRoomId.current} -> ${room.roomId}`);
      prevRoomId.current = room.roomId;
    }
  }, [room.roomId]);

  // Счётчик участников тот же, что у присутствия — но теперь включает нас.
  useEffect(() => {
    const recompute = () => {
      // СЧИТАЕМ ЛЮДЕЙ, А НЕ УСТРОЙСТВА (11.09).
      //
      // memberships — по УСТРОЙСТВАМ: один человек с ноутбука и с iPad даёт
      // ДВА членства. Подсчёт по длине массива ломает сразу три вещи:
      //   • «Отменить звонок» не появляется (условие participants <= 1);
      //   • «остался один» не срабатывает никогда;
      //   • немедленное завершение при уходе собеседника не срабатывает.
      // А тестируют звонки именно так — двумя своими устройствами.
      const alive = session.memberships.filter((m) => !m.isExpired());
      setParticipants(new Set(alive.map((m) => m.userId)).size);
    };
    recompute();
    session.on(MatrixRTCSessionEvent.MembershipsChanged, recompute);
    return () => {
      session.off(MatrixRTCSessionEvent.MembershipsChanged, recompute);
    };
  }, [session]);

  /**
   * ЗАМЕРНЫЙ ЛОГ + всплытие ошибки членства (дополнение к постановке,
   * Momo 12:14: «групповой звонок не работает вовсе»).
   *
   * Замер Momo по логам: инициатор получил токен и вошёл в LiveKit-комнату
   * ОДИН, остальные к /sfu/get не обращались. Одна из веток: state-событие
   * членства (m.call.member) у инициатора НЕ записалось — MembershipManager
   * упал (права, rate limit, сеть), а мы молча сидели «in-call», потому что
   * LiveKit-подключение от членства не зависит. MembershipManagerError —
   * единственный канал, где эта ошибка всплывает (сигнатура сверена по
   * MatrixRTCSession.d.ts: MembershipManagerError(error: unknown)).
   *
   * Лог всегда (для замера на стенде), плашка с ошибкой — только если мы
   * сейчас входим/в звонке: чужая ошибка нам неинтересна, а свою человек
   * обязан увидеть вместо лживого «Вы в звонке · 1 участник».
   */
  useEffect(() => {
    const onMembershipError = (e: unknown) => {
      diagnostics.add("matrixrtc-membership-error", { error: e });
      console.error(`[call] membership manager error room=${room.roomId}:`, e);
      setState((s) => {
        if (s === "joining" || s === "in-call") {
          setError(
            tf("rtc.callMembershipFailed", {
              error: (e as Error)?.message ?? String(e),
            }),
          );
          return "error";
        }
        return s;
      });
    };
    session.on(MatrixRTCSessionEvent.MembershipManagerError, onMembershipError);
    return () => {
      session.off(MatrixRTCSessionEvent.MembershipManagerError, onMembershipError);
    };
  }, [session, room.roomId, diagnostics]);

  /**
   * Полный выход: LiveKit disconnect + снятие членства. Безопасно вызывать
   * повторно и при полуоткрытом состоянии (ошибка на середине join).
   *
   * ПОЧЕМУ порядок «disconnect, потом leaveRoomSession»: пока мы ещё
   * числимся участником, отключение медиа выглядит для остальных как
   * «замолчал», а не «вышел и снова мигнул». Снятие членства последним
   * гасит плашку у других уже начисто.
   */
  const teardown = useCallback(async () => {
    diagnostics.add("leave-start", { state: livekitRef.current?.state });
    debugLogL(`[call] teardown() invoked room=${room.roomId} joined=${joinedRef.current}`);
    // Cancel every local ringtone immediately. If this device originated the
    // still-fresh call notification, also publish an explicit call-scoped
    // hangup so the other devices do not wait for sticky membership/TTL.
    stopRinging();
    await terminateActiveCall(client, room);
    const lk = livekitRef.current;
    livekitRef.current = null;
    if (lk) {
      try {
        await lk.disconnect();
        diagnostics.add("room-disconnected", { state: lk.state });
      } catch (e) {
        diagnostics.add("room-disconnect-error", { error: e, state: lk.state });
        // disconnect при уже разорванном соединении бросает — глотаем,
        // выход всё равно продолжаем: членство снять важнее.
      }
    }
    const mic = micStreamRef.current;
    micStreamRef.current = null;
    if (mic) mic.getTracks().forEach((t) => t.stop());
    // STEP6: камеру гасим так же, как микрофон — иначе индикатор камеры
    // горит после выхода из звонка (та же гоча, что у микрофона).
    const cam = camStreamRef.current;
    camStreamRef.current = null;
    camTrackRef.current = null;
    if (cam) cam.getTracks().forEach((t) => t.stop());
    // ЭКРАН гасим так же, как камеру: без этого системная плашка
    // «вы делитесь экраном» осталась бы висеть после выхода из звонка.
    const scr = screenStreamRef.current;
    screenStreamRef.current = null;
    screenTrackRef.current = null;
    cameraWasOnBeforeScreenRef.current = false;
    cameraDisabledForScreenRef.current = false;
    if (scr) scr.getTracks().forEach((t) => t.stop());
    setScreenTrack(null);
    screenBusyRef.current = false;
    setScreenBusy(false);
    setScreenError(null);
    setVideoTiles([]);
    setActiveSpeakerIdentity(null);
    setCallParticipantIdentities([]);
    cameraOnRef.current = false;
    setCameraOn(false);
    // Состояние добавления камеры — тоже часть звонка: иначе ошибка
    // «камера занята» всплывёт в следующем звонке.
    cameraBusyRef.current = false;
    setCameraBusy(false);
    setCameraError(null);
    setCameraInitialized(false);
    setMicOn(true);
    setIsVideo(false);
    isVideoRef.current = false;
    for (const el of audioElsRef.current.values()) {
      el.srcObject = null;
      el.remove();
    }
    audioElsRef.current.clear();
    if (joinedRef.current) {
      joinedRef.current = false;
      // Отмечаем СВОЙ выход: пока чужие ещё в звонке, он не должен
      // прилетать мне обратно как входящий (Антон 15:51 — «вышел из группы,
      // а телефон начинает звонить»; «повесил трубку, у второго гудок»).
      markSelfLeftCall(room.roomId);
      try {
        // Таймаут 5с: leaveRoomSession без сети не резолвится никогда,
        // а уходить из состояния «in-call» надо и при обрыве.
        await session.leaveRoomSession(5000);
        diagnostics.add("matrixrtc-leave-success");
      } catch (e) {
        diagnostics.add("matrixrtc-leave-failure", { error: e });
        // См. выше: повторная отправка ухода — забота MembershipManager,
        // он ретраит сам. Мы просто не вечны.
      }
    }
    diagnostics.add("leave-complete");
  }, [client, room, session, diagnostics]);

  // Смена комнаты или размонтирование — обязаны выйти начисто. Иначе при
  // переходе в другую комнату человек останется «в звонке» для всех.
  //
  // КРИТИЧЕСКИЙ БАГ 2026-09-12 (Антон: «сменил комнату — звонок оборвался»):
  // этот эффект срабатывал и при ОБЫЧНОЙ смене комнаты, потому что
  // RoomView размонтируется (ErrorBoundary key={roomId} в App), а звонок
  // жил внутри него. Теперь звонок принадлежит уровню App (держатель
  // передаёт persistOnUnmount=true), и размонтирование держателя значит
  // только одно: реальный выход из приложения/аккаунта — очистка нужна.
  // Поэтому при persistOnUnmount эффект НЕ ставится совсем, а очистка
  // висит на beforeunload (закрытие вкладки — единственный случай, когда
  // App умирает не через кнопку «Выйти»).
  //
  // Почему это безопасно против УТЕЧКИ (человек останется «в звонке»
  // для всех):
  //   • «Выйти»/«Отменить»/автовыходы («никто не ответил», «все ушли»)
  //     вызывают leave() → тот же teardown — они от persistOnUnmount не
  //     зависят и работают как раньше;
  //   • закрытие/обновление вкладки — beforeunload ниже (запись state-
  //     события уходит, пока страница выгружается; если совсем не успеет —
  //     членство протухнет по expires, как и при крэше до этой правки);
  //   • useCallJoin по-прежнему живёт в ОДНОМ экземпляре на клиент
  //     (один держатель в App) — второго хука с вторым членством нет.
  useEffect(() => {
    if (persistOnUnmount) return;
    return () => {
      debugLogL(`[call] teardown effect cleanup room=${room.roomId} joined=${joinedRef.current}`);
      void teardown();
    };
  }, [teardown, persistOnUnmount, room.roomId]);

  // Закрытие вкладки/браузера — единственный путь, где снять членство
  // можем только мы и только сейчас. Ставим слушатель только в режиме
  // persistOnUnmount: в обычном режиме эту работу делает эффект выше.
  useEffect(() => {
    if (!persistOnUnmount) return;
    const onUnload = () => {
      debugLogL(`[call] beforeunload teardown room=${room.roomId} joined=${joinedRef.current}`);
      void teardown();
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [persistOnUnmount, teardown, room.roomId]);

  /**
   * E2EE-ПРОВЕРКА ПЕРЕД ВХОДОМ (задача Антона 2026-09-14).
   *
   * В зашифрованной комнате MatrixRTC пытается шифровать события
   * звонка (m.rtc.member), но crypto в этом клиенте выключен намеренно
   * (см. заголовок файла). joinRoomSession падает внутри SDK, а ошибка
   * гасилась пустым catch в MembershipManager — человек видел молчание.
   *
   * Теперь проверяем ДО входа: если комната зашифрована — сразу
   * показываем понятную ошибку, не пытаясь войти. Это не «запрет
   * навсегда», а «сейчас не поддерживается» — когда включим crypto,
   * проверку уберём.
   *
   * ПРИЗНАК — isEncrypted(room), а НЕ room.hasEncryptionStateEvent():
   * мессенджер читает m.room.encryption напрямую из state, не трогая
   * crypto-слой (см. комментарий в rooms.ts). hasEncryptionStateEvent
   * живёт в crypto-слое и у нас сознательно не используется.
   */
  const roomEncrypted = isEncrypted(room);

  const join = useCallback((withVideoArg: unknown = false) => {
    // ЖЁСТКОЕ ПРИВЕДЕНИЕ К БУЛЕВУ (баг Антона 15:58: «с обеих кнопок звонок
    // с видео»). Причина: onClick={onJoin} передаёт в первый аргумент
    // MouseEvent, а он truthy — то есть обычная кнопка «Присоединиться»
    // включала камеру. Место вызова чиню отдельно, но ядро обязано быть
    // устойчивым само: аргумент считается «с видео» ТОЛЬКО если это
    // настоящий true.
    const withVideo = withVideoArg === true;
    // Повторный клик во время joining/in-call игнорируем: двойной вход
    // создал бы вторую LiveKit-комнату и второе членство на то же устройство.
    if (joinedRef.current || livekitRef.current) return;

    // E2EE: в зашифрованной комнате звонок сейчас не работает (crypto
    // выключен). Не пытаемся войти — сразу говорим человеку почему.
    if (roomEncrypted) {
      diagnostics.reset();
      diagnostics.add("join-failure", { detail: "encrypted-room" });
      setError(
        tr("ui.zvonki_v_zashifrovannyh_komnatah_poka_ne") +
          tr("ui.sozdayte_obychnuyu_komnatu_ili_dozhdites_o"),
      );
      setState("error");
      return;
    }

    diagnostics.reset();
    diagnostics.add("join-start", { detail: withVideo ? "video" : "audio" });
    debugLogL(`[call] join() invoked room=${room.roomId} video=${withVideo}`);
    isVideoRef.current = withVideo;
    setIsVideo(withVideo);
    setState("joining");
    setError(null);

    (async () => {
      const lk = new LivekitRoom({
        // Аудио всегда; камера — только когда вход по кнопке 📹 (STEP6).
        // adaptiveStream/dynacast не трогаем — дефолты SDK нормальны.
        // Эхоподавление/шумодав/АРУ — явно (16.10), на случай
        // захвата силами самого LiveKit.
        audioCaptureDefaults: { ...MIC_PROCESSING },
      });
      elementSinkRef.current = "";
      livekitRef.current = lk;

      // Чужие аудиотреки — в скрытый <audio>-элемент. Без этого трек
      // подписан, но нигде не звучит: livekit сам элементы не создаёт.
      // ДИАГНОСТИКА чужих треков (баг Антона 16:06: «видео уходит только от
      // звонящего, от других участников не приходит»). Если подписка вообще
      // не происходит, в консоли не будет ни одной строки [call] track-*.
      // Browser autoplay policy can block remote audio even though tracks are
      // subscribed. Make that VISIBLE in diagnostics and retry playback on the
      // next user gesture (LiveKit requires startAudio() from a gesture).
      lk.on(RoomEvent.AudioPlaybackStatusChanged, (playing) => {
        diagnostics.add("audio-playback-status", { detail: playing ? "playing" : "blocked" });
        if (!playing) {
          const resume = () => {
            lk.startAudio().then(
              () => diagnostics.add("audio-playback-status", { detail: "resumed-after-gesture" }),
              () => diagnostics.add("audio-playback-status", { detail: "resume-failed" }),
            );
          };
          document.addEventListener("pointerdown", resume, { once: true });
        }
      });
      lk.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        // LiveKit выдаёт участников в порядке audioLevel. Это реальный
        // speaker detector SFU/SDK, не таймер и не эвристика интерфейса.
        // Пустой список очищает рамку сразу после тишины/mute.
        setActiveSpeakerIdentity(speakers[0]?.identity ?? null);
      });
      lk.on(RoomEvent.TrackPublished, (pub, participant) => {
        diagnostics.add("remote-track-published", {
          participant: diagnostics.participant(participant.identity),
          kind: pub.kind,
          source: pub.source,
          muted: pub.isMuted,
        });
        console.info(
          `[call] remote published: ${pub.kind}/${pub.source} from ${participant.identity}`,
        );
      });
      lk.on(RoomEvent.ParticipantConnected, (participant) => {
        diagnostics.add("participant-connected", {
          participant: diagnostics.participant(participant.identity),
        });
        setCallParticipantIdentities((prev) =>
          prev.includes(participant.identity) ? prev : [...prev, participant.identity],
        );
        console.info(`[call] participant connected: ${participant.identity}`);
      });
      lk.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
        diagnostics.add("remote-track-subscribed", {
          participant: diagnostics.participant(participant.identity),
          kind: track.kind,
          source: track.source,
          muted: track.isMuted,
        });
        console.info(
          `[call] track subscribed: ${track.kind}/${track.source} from ${participant.identity}`,
        );
        if (track.kind === "audio") {
          const key = track.sid ?? `${participant.identity}:${track.source}`;
          let el = audioElsRef.current.get(key);
          if (!el) {
            el = document.createElement("audio");
            el.autoplay = true;
            audioElsRef.current.set(key, el);
          }
          track.attach(el);
          // WebKit-путь (баг 1): LiveKit сам sink не ставит, ставим мы —
          // иначе новый собеседник звучал бы в системном выходе (ТВ).
          if (!supportsAudioOutputSelection() && elementSinkRef.current) {
            void setElementSink(el, elementSinkRef.current).catch((e) =>
              diagnostics.add("device-switch", { kind: "speaker", detail: "attach-sink-failure", error: e }),
            );
          }
          diagnostics.add("remote-audio-attached", {
            participant: diagnostics.participant(participant.identity),
            detail: `elements=${audioElsRef.current.size}`,
          });
          return;
        }
        // STEP6: чужое ВИДЕО. Раньше здесь стояло
        // `if (track.kind !== "audio") return;` — видео выбрасывалось.
        // Теперь отдаём тайлом в раскладку; элемент <video autoplay
        // playsinline> создаёт компонент (RoomView), playsinline
        // обязателен: без него Safari разворачивает видео на весь экран.
        if (track.kind === "video") {
          // LiveKit обычно не присылает локальную публикацию как subscribed,
          // но повторная сигнализация/совместимость клиентов не должна
          // превратить наше screen preview во вторую «remote» плитку.
          if (participant.identity === lk.localParticipant.identity) return;
          // КАМЕРА и ДЕМОНСТРАЦИЯ ЭКРАНА — ДВЕ РАЗНЫЕ плитки одного
          // участника (screen share, Антон 12.09 12:28). Различаем по
          // track.source: ScreenShare → ключ "screen:<id>" и подпись
          // «Экран: Имя» (дословно из постановки); всё остальное —
          // камера с ключом "cam:<id>". Без различения по source вторая
          // дорожка того же человека либо затирала бы первую, либо
          // отбрасывалась проверкой на дубль.
          const isScreen = track.source === Track.Source.ScreenShare;
          const key = `${isScreen ? "screen" : "cam"}:${participant.identity}`;
          const stream = new MediaStream([track.mediaStreamTrack]);
          // Same identity→user rule as callParticipants: the identity is not a
          // Matrix user ID, so resolve it via the RTC membership first and the
          // legacy "user:device" shape second before falling back to raw text.
          const tileUserId =
            session.memberships.find(
              (m) => !m.isExpired() && m.rtcBackendIdentity === participant.identity,
            )?.userId ?? userIdFromLegacyIdentity(participant.identity);
          const memberName =
            (tileUserId ? room.getMember(tileUserId)?.name : null) ??
            tileUserId ??
            participant.identity;
          const label = isScreen ? tf("rtc.screenOf", { name: memberName }) : memberName;
          setVideoTiles((prev) => {
            if (prev.some((t) => t.key === key)) return prev;
            return [
              ...prev,
              {
                key,
                stream,
                local: false,
                label,
                participantIdentity: participant.identity,
                speaking: false,
                kind: isScreen ? ("screen" as const) : ("camera" as const),
              },
            ];
          });
        }
      });
      lk.on(RoomEvent.TrackSubscriptionFailed, (_trackSid, participant) => {
        diagnostics.add("remote-track-subscription-failed", {
          participant: diagnostics.participant(participant.identity),
        });
      });
      lk.on(RoomEvent.TrackUnsubscribed, (track, _pub, participant) => {
        diagnostics.add("remote-track-unsubscribed", {
          participant: diagnostics.participant(participant.identity),
          kind: track.kind,
          source: track.source,
          muted: track.isMuted,
        });
        if (track.kind === "audio") {
          track.detach();
          const key = track.sid ?? `${participant.identity}:${track.source}`;
          const el = audioElsRef.current.get(key);
          if (el) {
            el.srcObject = null;
            el.remove();
            audioElsRef.current.delete(key);
          }
          setActiveSpeakerIdentity((current) =>
            current === participant.identity ? null : current,
          );
          return;
        }
        if (track.kind === "video") {
          track.detach();
          // Убираем РОВНО ту плитку, чей источник отписался: человек мог
          // выключить экран, оставив камеру, и наоборот.
          const isScreen = track.source === Track.Source.ScreenShare;
          const key = `${isScreen ? "screen" : "cam"}:${participant.identity}`;
          setVideoTiles((prev) => prev.filter((t) => t.key !== key));
        }
      });
      lk.on(RoomEvent.TrackMuted, (pub, participant) => {
        diagnostics.add("track-muted", {
          participant: participant.isLocal ? "local" : diagnostics.participant(participant.identity),
          kind: pub.kind,
          source: pub.source,
          muted: true,
        });
        if (pub.source === Track.Source.Camera) bumpMicState();
        if (pub.source === Track.Source.Microphone) {
          bumpMicState();
          setActiveSpeakerIdentity((current) =>
            current === participant.identity ? null : current,
          );
          // Если LiveKit не смог перезахватить микрофон после потери
          // устройства, он глушит трек сам. Кнопка обязана показать
          // «выключен», чтобы нажатие включило (и перезахватило) вход,
          // а не «выключило» уже мёртвый.
          if (participant.isLocal) setMicOn(false);
        }
      });
      lk.on(RoomEvent.TrackUnmuted, (pub, participant) => {
        diagnostics.add("track-unmuted", {
          participant: participant.isLocal ? "local" : diagnostics.participant(participant.identity),
          kind: pub.kind,
          source: pub.source,
          muted: false,
        });
        if (pub.source === Track.Source.Microphone || pub.source === Track.Source.Camera) bumpMicState();
        if (participant.isLocal && pub.source === Track.Source.Microphone) setMicOn(true);
      });
      // Появление/исчезновение аудиопубликации тоже меняет индикатор:
      // участник без опубликованного микрофона считается «выключен».
      const onMicPublicationChange = (pub: { source: Track.Source }) => {
        // Камера — тот же механизм: заглушка «камера выключена».
        if (pub.source === Track.Source.Microphone || pub.source === Track.Source.Camera) bumpMicState();
      };
      lk.on(RoomEvent.TrackPublished, onMicPublicationChange);
      lk.on(RoomEvent.TrackUnpublished, onMicPublicationChange);
      lk.on(RoomEvent.LocalTrackPublished, onMicPublicationChange);
      lk.on(RoomEvent.LocalTrackUnpublished, onMicPublicationChange);
      // Участник вышел совсем — его видеотайл тоже убираем (TrackUnsubscribed
      // обычно приходит раньше, но страхуемся от «зависшего» кадра).
      lk.on(RoomEvent.ParticipantDisconnected, (participant) => {
        diagnostics.add("participant-disconnected", {
          participant: diagnostics.participant(participant.identity),
        });
        // Уходят ОБЕ его плитки: и камера, и экран.
        setVideoTiles((prev) =>
          prev.filter((t) => !t.key.endsWith(`:${participant.identity}`)),
        );
        setCallParticipantIdentities((prev) =>
          prev.filter((identity) => identity !== participant.identity),
        );
        setActiveSpeakerIdentity((current) =>
          current === participant.identity ? null : current,
        );
      });
      // Сервер разорвал соединение (кик, выключение SFU) — выходим честно,
      // а не висим «в звонке» без медиа.
      lk.on(RoomEvent.Disconnected, () => {
        diagnostics.add("room-disconnected-event", { state: lk.state });
        void teardown().finally(() => {
          setState("idle");
        });
      });
      lk.on(RoomEvent.ConnectionStateChanged, (connectionState) => {
        diagnostics.add("room-connection-state", { state: connectionState });
      });
      // LiveKit на devicechange сам выбирает устройства (selectDefaultDevices)
      // и только ПОТОМ шлёт MediaDevicesChanged. На Chromium он может увести
      // выход на новое «первое» устройство (ТВ) после того, как наш эффект
      // выбора динамика уже отработал. Перечитываем список ещё раз —
      // эффект выше вернёт выбор человека (баг 1, 16.10).
      lk.on(RoomEvent.MediaDevicesChanged, () => {
        setDevicesPermissionBump((n) => n + 1);
      });
      lk.on(RoomEvent.MediaDevicesError, (mediaError) => {
        diagnostics.add("media-device-error", { error: mediaError });
      });
      // Подтверждение переключения устройства (switchActiveDevice) —
      // синхронизируем наше состояние с фактом. Это единственный
      // достоверный источник «какое устройство реально активно»: до него
      // мы лишь оптимистично обновили меню в selectDevice.
      lk.on(RoomEvent.ActiveDeviceChanged, (kind, deviceId) => {
        const slot: DeviceSlot | null =
          kind === "audioinput" ? "mic" : kind === "videoinput" ? "camera" : kind === "audiooutput" ? "speaker" : null;
        if (!slot) return;
        // Браузерный алиас "default" у нас представлен "" (явный пункт
        // «По умолчанию» в меню) — нормализуем, иначе галочка в меню
        // съедет на несуществующий пункт.
        const normalized = deviceId === "default" ? "" : deviceId;
        setActiveDevices((prev) =>
          prev[slot] === normalized ? prev : { ...prev, [slot]: normalized },
        );
        // LiveKit сам перезахватил микрофон (ended / devicechange):
        // держим raw ref на новой дорожке, иначе teardown гасит старую.
        if (slot === "mic") {
          const current = lk.localParticipant.getTrackPublication(Track.Source.Microphone)?.track?.mediaStreamTrack;
          if (current && micStreamRef.current?.getAudioTracks()[0] !== current) {
            micStreamRef.current = new MediaStream([current]);
            diagnostics.add("device-switch", { kind: "mic", detail: "auto-reacquired" });
          }
        }
      });

      try {
        // 1. Микрофон — ПОКА ЖЕСТ ТЁПЛЫЙ, но СЫРЫМ getUserMedia, а не
        //    setMicrophoneEnabled.
        //
        //    Баг Антона 10.09 18:59: «publishing rejected as engine not connected
        //    within timeout». setMicrophoneEnabled НЕ ставит публикацию в очередь
        //    до connect(), как мы рассчитывали: он ждёт готовый engine с своим
        //    таймаутом и падает раньше, чем мы успеваем взять токен и подключиться.
        //
        //    getUserMedia же ничего не знает про LiveKit: он просто отдаёт трек
        //    пока жест тёплый. Трек держим у себя и публикуем ПОСЛЕ connect,
        //    когда engine уже жив и отказывать ему не от чего.
        //
        //    ЗАМЕР ДЕФЕКТА №2 (Антон 11.09 12:14: «разрешение на микрофон
        //    спрашивается каждый раз»). Причина на момент кода НЕ установлена
        //    (две ветки: политика Safari «доступ на одну сессию страницы» или
        //    наш повторный getUserMedia). Не угадываем — измеряем: печатаем
        //    состояние Permission API ДО и ПОСЛЕ getUserMedia. Если на втором
        //    звонке state всё ещё "prompt" — решение о повторном запросе
        //    принимает браузер (смотри настройку сайта), а не наш код. Лог
        //    читается в консоли Safari: Develop → Show Web Inspector.
        try {
          const perm = await navigator.permissions.query({
            name: "microphone" as PermissionName,
          });
          debugLog(`[call] mic permission before getUserMedia: ${perm.state}`);
        } catch (e) {
          // Safari старых версий не знает "microphone" в Permission API —
          // это тоже данные для вывода, печатаем, а не глотаем.
          debugLog(`[call] mic permission query unsupported: ${String(e)}`);
        }
        const micStream = await navigator.mediaDevices.getUserMedia({
          audio: deviceConstraint("audio") ?? { ...MIC_PROCESSING },
        });
        micStreamRef.current = micStream;
        // Разрешение на микрофон выдано (или уже было) — enumerateDevices
        // теперь отдаст настоящие label. Перечитываем списки устройств.
        setDevicesPermissionBump((n) => n + 1);
        const micTrack = micStream.getAudioTracks()[0];
        if (!micTrack) throw new Error(tr("ui.mikrofon_ne_dal_audiodorozhku"));
        diagnostics.add("local-track-captured", {
          kind: micTrack.kind,
          source: "microphone",
          muted: !micTrack.enabled,
        });
        // Эхо: что браузер ФАКТИЧЕСКИ включил (а не что мы попросили).
        {
          const st = micTrack.getSettings?.() ?? {};
          const flag = (v: unknown) => (v === true ? "on" : v === false ? "off" : "na");
          diagnostics.add("mic-processing", {
            detail: `aec-${flag(st.echoCancellation)}.ns-${flag(st.noiseSuppression)}.agc-${flag(st.autoGainControl)}`,
          });
        }
        try {
          const perm = await navigator.permissions.query({
            name: "microphone" as PermissionName,
          });
          debugLog(`[call] mic permission after getUserMedia: ${perm.state}`);
        } catch {
          // см. выше
        }

        // 1б. STEP6: камера — только когда вход по 📹. Тот же порядок, что
        //    у микрофона (он выстрадан, не менять): захват ДО connect,
        //    публикация ПОСЛЕ. Отказ браузера показываем текстом, а не
        //    молча — как у микрофона (Permission API замер + ошибка в UI).
        let camTrack: MediaStreamTrack | null = null;
        if (withVideo) {
          try {
            const perm = await navigator.permissions.query({
              name: "camera" as PermissionName,
            });
            debugLog(`[call] camera permission before getUserMedia: ${perm.state}`);
          } catch (e) {
            debugLog(`[call] camera permission query unsupported: ${String(e)}`);
          }
          const camStream = await navigator.mediaDevices.getUserMedia({
            video: deviceConstraint("video") ?? true,
          });
          camStreamRef.current = camStream;
          setDevicesPermissionBump((n) => n + 1);
          camTrack = camStream.getVideoTracks()[0] ?? null;
          if (!camTrack) throw new Error(tr("ui.kamera_ne_dala_videodorozhku"));
          camTrackRef.current = camTrack;
          diagnostics.add("local-track-captured", {
            kind: camTrack.kind,
            source: "camera",
            muted: !camTrack.enabled,
          });
          try {
            const perm = await navigator.permissions.query({
              name: "camera" as PermissionName,
            });
            debugLog(`[call] camera permission after getUserMedia: ${perm.state}`);
          } catch {
            // см. выше
          }
        }

        // 2. Членство MatrixRTC — чтобы другие увидели нас в звонке.
        //    joinRoomSession (deprecated-вариант) берёт identity из клиента
        //    сам; современный joinRTCSession требует собрать
        //    CallMembershipIdentityParts руками — на этом шаге deprecated-
        //    обёртка короче и делает ровно то же. Transport — наш livekit.
        //
        //    КРИТИЧНО (дефект «звонки в комнату не работают», Антон 12:54):
        //    joinRoomSession может быть асинхронным (возвращать Promise).
        //    Если не await'нуть, членство ставится в очередь, но не гарантированно
        //    записывается до того, как мы уйдём за токеном и в LiveKit. При
        //    быстром входе/выходе членство теряется — остальные не видят нас.
        const transport: LivekitTransport = {
          type: "livekit",
          livekit_service_url: livekitJwtUrl().replace(/\/sfu\/get$/, ""),
          livekit_alias: await livekitAliasFor(room.roomId),
        };

        // The JWT service receives the real Matrix room ID and derives the
        // same deterministic alias itself. The advertised alias is used only
        // by MatrixRTC peers to verify that they selected the same media room.
        diagnostics.add("matrixrtc-transport-selected", { detail: "matrixrtc-standard" });

        // notificationType "ring" + lifetime: инициатор звонка отправляет
        // в комнату событие org.matrix.msc4075.rtc.notification (MSC4075),
        // на которое принимающий отвечает org.matrix.msc4310.rtc.decline
        // (MSC4310) при отказе. Без notificationType уведомление не
        // отправляется, и отклонять нечего. notificationLifetimeMs —
        // сколько звонить до автоматического таймаута (60 секунд).
        // Capture the foreign membership before joining. This distinguishes a
        // caller that was never visible from one that disappears when our own
        // membership format is published.
        // УБОРКА СВОИХ ФАНТОМОВ — СТРОГО ДО joinRoomSession.
        //
        // ПРИЧИНА (замер 26.09 на боевом Synapse + код SDK):
        // `MatrixRTCSession.js:263` отправляет ring ТОЛЬКО тому, кто вошёл в
        // ПУСТУЮ сессию (`oldMemberships.length === 0`). Одно забытое членство
        // закрытой вкладки держит сессию «непустой» 4 часа — и звонок не
        // уведомляет НИКОГО, ни в нашем клиенте, ни в Element X.
        //
        // Чистим только СВОИ членства с ДРУГИХ устройств. Ошибка уборки
        // звонок не отменяет: хуже, чем есть, не станет.
        try {
          const cleared = await clearStaleOwnCallMemberships(client, room);
          if (cleared > 0) {
            diagnostics.add("stale-membership-cleared", { detail: String(cleared) });
            debugLogL(`[call] cleared ${cleared} stale own membership(s) room=${room.roomId}`);
          }
        } catch (e) {
          diagnostics.add("stale-membership-cleanup-failed", { error: e });
        }

        // ЗАЛИПШИЙ MembershipManager — ВТОРАЯ ПРИЧИНА МОЛЧАНИЯ ZVONKA.
        //
        // ЗАМЕР 19:48:15 (первый тест Антона на новом бандле):
        // клиент отрапортовал join-success, но на сервере ЗА ЭТОТ ВХОД НЕТ
        // НИ членства, НИ ring — при том что в 19:47:20 и 19:48:02 ring уходил.
        //
        // Причина в SDK, `MatrixRTCSession.js:332`:
        //     joinRTCSession(...) { if (this.isJoined()) { ...; return; } ... }
        // При раннем выходе НЕ ставится `pendingNotificationToSend` и НЕ
        // публикуется членство, то есть ring не рождается ВООБЩЕ. После
        // быстрого цикла звонок→отбой→звонок (четыре за минуту в том тесте)
        // внутренний MembershipManager остаётся в состоянии joined, хотя на
        // сервере членство уже снято (19:48:08 — пустой content).
        //
        // ВАЖНО: наш собственный диагностический "matrixrtc-join-success"
        // пишется БЕЗУСЛОВНО после вызова и НЕ доказывает вход — именно
        // поэтому лог клиента выглядел успешным. Ниже состояние сверяется
        // фактом `isJoined()`, а не нашей верой в успех.
        if (session.isJoined()) {
          diagnostics.add("stale-session-detected", { detail: "isJoined before join" });
          debugLogL(`[call] SDK still joined before join room=${room.roomId} - forcing leave`);
          try {
            await session.leaveRoomSession(5000);
            diagnostics.add("stale-session-cleared");
          } catch (e) {
            diagnostics.add("stale-session-clear-failed", { error: e });
          }
        }

        addMatrixRtcTransportSnapshot(session, client, transport, diagnostics, "before-join");
        // Do not pass `transport` as multiSfuFocus here. In matrix-js-sdk
        // 42.3.0 that switches the legacy membership to `focus_selection:
        // "multi_sfu"`; omitting it selects the backwards-compatible
        // `oldest_membership` mode. We publish one transport, not multiple
        // simultaneous SFUs, and Element X compatibility relies on every
        // member resolving the oldest member's advertised LiveKit focus.
        session.joinRoomSession([transport], undefined, {
          // Keep publishing legacy m.call.member state events. Our deployed
          // server path and call-card parser are compatible with this format,
          // while the SDK still reads both legacy and sticky memberships.
          // STEP6: intent по кнопке входа — "video" для 📹, "audio" для 📞.
          // Принимающая сторона читает его из rtc.notification
          // ("m.call.intent") и показывает «Входящий видеозвонок».
          callIntent: withVideo ? "video" : "audio",
          notificationType: "ring",
          notificationLifetimeMs: 60_000,
        });
        joinedRef.current = true;
        // СВЕРЯЕМ ФАКТ, А НЕ НАМЕРЕНИЕ. Раньше здесь безусловно писалось
        // "join-success", и лог утверждал успех даже тогда, когда SDK вышел
        // первой строкой и ничего не отправил (замер 19:48:15).
        if (session.isJoined()) {
          diagnostics.add("matrixrtc-join-success");
        } else {
          diagnostics.add("matrixrtc-join-NOT-confirmed", {
            detail: "isJoined() false after joinRoomSession - ring will NOT be sent",
          });
          debugLogL(`[call] JOIN NOT CONFIRMED room=${room.roomId} - no membership, no ring`);
        }
        addMatrixRtcTransportSnapshot(session, client, transport, diagnostics, "after-join");
        // Вошёл сам — прежняя отметка о выходе больше не нужна.
        clearSelfLeftOnRejoin(room.roomId);
        debugLogL(`[call] joinRoomSession completed room=${room.roomId}`);

        // 3. Токен у JWT-сервиса. OpenID-токен Synapse — доказательство,
        //    что мы владеем Matrix-аккаунтом; сервис сверяет его с
        //    homeserver'ом перед выдачей LiveKit-JWT.
        //
        //    БАГ 14.09 (401 при входе в звонок): getOpenIdToken() уходил
        //    на протухшем access-токене MAS (живёт 300 с) — refresh не
        //    отрабатывал, и только перелогин лечил. Здесь это чинится в
        //    ДВА слоя: (а) proactive-refresh ПЕРЕД вызовом, (б) ОДИН
        //    прозрачный повтор по 401 с force-refresh. Два 401 подряд =
        //    настоящая проблема авторизации — её видит человек, а не
        //    молчаливый отказ (правило постановки).
        const requestLivekitJwt = async (): Promise<{ url: string; jwt: string }> => {
          const openid = await client.getOpenIdToken();
          const res = await fetch(livekitJwtUrl(), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              room: room.roomId,
              openid_token: openid,
              device_id: client.getDeviceId(),
            }),
          });
          if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(
              tf("error.jwtHttp", { status: res.status }) +
                (text ? ` — ${text.slice(0, 200)}` : ""),
            );
          }
          const data = (await res.json()) as { url?: string; jwt?: string };
          if (!data.url || !data.jwt) {
            throw new Error(tr("ui.jwt_servis_v_otvete_net_url_jwt"));
          }
          return { url: data.url, jwt: data.jwt };
        };

        // (а) Свежий токен ДО первого вызова: если до истечения < 60 с —
        //     обновляем и дожидаемся. Общий inflight-promise внутри
        //     защищает от двойного refresh при одновременных входах.
        await ensureFreshAccessToken();

        let tokenData: { url: string; jwt: string };
        try {
          tokenData = await requestLivekitJwt();
        } catch (firstError) {
          // (б) ОДИН прозрачный повтор, и только при авторизационной
          //     причине (HTTP 401 от /sfu/get либо MatrixError с httpStatus
          //     401 / errcode M_UNKNOWN_TOKEN от getOpenIdToken). Сетевые
          //     и прочие ошибки повторять не пытаемся — это не наш случай.
          const is401 = (): boolean => {
            const msg = firstError instanceof Error ? firstError.message : "";
            if (/HTTP 401\b/.test(msg)) return true;
            const err = firstError as {
              httpStatus?: number;
              errcode?: string;
            };
            return err.httpStatus === 401 || err.errcode === "M_UNKNOWN_TOKEN";
          };
          if (!is401()) throw firstError;

          console.warn(
            "[call] token request got 401 — forcing access-token refresh and retrying once",
          );
          await ensureFreshAccessToken({ force: true });
          tokenData = await requestLivekitJwt();
        }

        // Второй 401 уже выбросится как Error из requestLivekitJwt — он
        // уйдёт в catch ниже и покажется человеку текстом, не молча.

        // 4. LiveKit connect.
        await lk.connect(tokenData.url, tokenData.jwt);
        setCallParticipantIdentities(
          [
            lk.localParticipant.identity,
            ...Array.from(lk.remoteParticipants.values(), (participant) => participant.identity),
          ].filter((identity, index, all) => Boolean(identity) && all.indexOf(identity) === index),
        );
        diagnostics.add("room-connect-success", { state: lk.state });
        addLivekitRemoteSnapshot(lk, diagnostics, "after-connect");
        addMatrixRtcTransportSnapshot(session, client, transport, diagnostics, "after-connect");
        window.setTimeout(() => {
          if (livekitRef.current !== lk) return;
          addLivekitRemoteSnapshot(lk, diagnostics, "after-3s");
          addMatrixRtcTransportSnapshot(session, client, transport, diagnostics, "after-3s");
        }, 3000);

        if (livekitRef.current !== lk) return; // вышли, пока коннектились

        // 4б. Сохранённый ДИНАМИК (persist, Антон 12.09). У динамика нет
        //     getUserMedia-фазы — он применяется к уже звучащим элементам
        //     через switchActiveDevice("audiooutput"). Применяем сразу после
        //     connect, ДО появления первого чужого трека: LiveKit помнит
        //     выбор и применит его к каждому новому audio-элементу сам.
        //     Устройство могло исчезнуть со вчерашнего звонка — exact=false
        //     даёт аккуратный откат на умолчание вместо исключения.
        const savedSpeaker = activeDevicesRef.current.speaker;
        // Путь + сколько выходов браузер реально отдал. Если в Mac-приложении
        // здесь outputs-0 — WKWebView не перечисляет динамики, и выбора
        // наушников в меню не будет (это предел WebKit, не нашего кода).
        void navigator.mediaDevices?.enumerateDevices?.().then((all) => {
          const outputs = all.filter((d) => d.kind === "audiooutput" && d.deviceId !== "default").length;
          diagnostics.add("speaker-path", {
            detail: `${supportsAudioOutputSelection() ? "livekit" : elementSinkSupported() ? "element" : "none"}.outputs-${outputs}`,
          });
        }).catch(() => diagnostics.add("speaker-path", { detail: "enumerate-failed" }));
        if (savedSpeaker) {
          void applySpeaker(lk, savedSpeaker).catch((e) => {
            diagnostics.add("device-switch", { kind: "speaker", detail: "restore-failure", error: e });
            console.warn("[call] restore speaker device failed");
          });
        }

        // 5. Теперь — и только теперь — публикуем микрофон. До connect
        //    этот вызов отвечал «engine not connected within timeout».
        //    МИКРОФОН ОТВАЛИВАЛСЯ ПРИ СМЕНЕ НАУШНИКОВ (Антон 12.10, Mac).
        //    Сырой MediaStreamTrack в publishTrack LiveKit оборачивает как
        //    userProvidedTrack=true, а такой трек SDK «не ведёт»: на ended
        //    он лишь делает mute(), unmute его не перезахватывает, а
        //    selectDefaultDevices на devicechange его пропускает. Итог —
        //    собеседники не слышат до перезапуска приложения. Оборачиваем
        //    сами с userProvidedTrack=false: тогда LiveKit на ended
        //    перезахватывает вход с устройства по умолчанию, на devicechange
        //    (WebKit/Safari) уходит с исчезнувшего на актуальное, а unmute
        //    перезахватывает завершённый трек. Mute — штатный isMuted трека,
        //    при restart он сохраняется (enabled = !isMuted).
        const managedMic = new LocalAudioTrack(micTrack, deviceConstraint("audio"), false);
        await lk.localParticipant.publishTrack(managedMic, { source: Track.Source.Microphone });
        diagnostics.add("local-track-published", {
          kind: micTrack.kind,
          source: "microphone",
          muted: !micTrack.enabled,
        });
        // STEP6: камеру публикуем тем же порядком — ПОСЛЕ connect, по
        // образцу микрофона. Track.Source.Camera — чтобы LiveKit/SFU
        // отличали её от демонстрации экрана.
        if (camTrack) {
          await lk.localParticipant.publishTrack(camTrack, { source: Track.Source.Camera });
          diagnostics.add("local-track-published", {
            kind: camTrack.kind,
            source: "camera",
            muted: !camTrack.enabled,
          });
          // Своя картинка — превью в раскладке (muted, звук туда не идёт:
          // в потоке только видеотрек камеры).
          setVideoTiles((prev) => [
            {
              key: "cam:local",
              stream: new MediaStream([camTrack]),
              local: true,
              label: tr("timeline.senderMe"),
              participantIdentity: lk.localParticipant.identity,
              speaking: false,
              kind: "camera" as const,
            },
            ...prev,
          ]);
          cameraOnRef.current = true;
          setCameraOn(true);
          setCameraInitialized(true);
        }

        setState("in-call");
        diagnostics.add("join-success", { state: lk.state });
      } catch (e) {
        diagnostics.add("join-failure", { error: e, state: lk.state });
        // Откат: иначе останемся числиться участником без медиа.
        await teardown();
        if (livekitRef.current === lk) livekitRef.current = null;
        setError(e instanceof Error ? e.message : String(e));
        setState("error");
      }
    })();
  }, [client, room, session, teardown, deviceConstraint, roomEncrypted, diagnostics, applySpeaker]);

  /**
   * МНЕ ОТКАЗАЛИ (MSC4310) — Антон 14:18: «у звонка нет возможности отменить»,
   * 13:17: «если хочу отказаться, должен ждать, пока инициатор положит трубку».
   *
   * Слушаем org.matrix.msc4310.rtc.decline в этой комнате. Показываем
   * «<имя> отклонил(а) звонок» и завершаем звонок сами — ждать нечего.
   *
   * ТРИ ФИЛЬТРА, без которых это уже ломалось 11.09 в 14:56:
   *   • toStartOfTimeline — при синхронизации и прокрутке сюда приходят
   *     СТАРЫЕ события; один отказ из прошлого теста иначе гасит каждый
   *     следующий звонок (именно так пропал звук);
   *   • возраст > 60 с — отказ не от текущего звонка;
   *   • sender === я — свой же отказ с другого устройства мне не адресован.
   */
  const [declinedBy, setDeclinedBy] = useState<string | null>(null);
  useEffect(() => {
    setDeclinedBy(null);
  }, [room.roomId]);

  const leave = useCallback(() => {
    void teardown().finally(() => {
      setState("idle");
      setError(null);
    });
  }, [teardown]);

  /**
   * Тумблер камеры вкл/выкл.
   *
   * ИСПРАВЛЕНО 2026-09-12 09:21 (Антон, голосом): «переключение с
   * голосового звонка на видеозвонок не проходит, камера всегда
   * заблокирована... если ты хочешь иметь возможность добавить видео,
   * ты должен эту возможность добавить... ты пошлёшь запрос на
   * доступ к камере».
   *
   * БЫЛО: в аудиозвонке `camTrackRef.current === null`, функция выходила
   * первой же строкой — кнопка нажималась вхолостую. Это шло от
   * уточнения Антона 11.09 14:19 («видео — отдельной кнопкой»), но
   * то уточнение было про СПОСОБ НАЧАТЬ звонок, а не про запрет
   * включить камеру внутри идущего разговора. 12.09 Антон это снял.
   *
   * СТАЛО — два разных пути:
   *  1. трек УЖЕ ЕСТЬ (видеозвонок или камеру уже включали) —
   *     mute/unmute через setCameraEnabled, устройство НЕ перезапрашиваем;
   *  2. трека НЕТ (чистый аудиозвонок) — ЗАПРАШИВАЕМ камеру
   *     (getUserMedia) и публикуем трек в идущий звонок.
   *
   * Почему запрос здесь безопасен, хотя в комментарии выше стояло
   * «повторный getUserMedia без клика браузер вправе отклонить»:
   * тумблер вызывается ИМЕННО кликом по кнопке — жест «тёплый»,
   * это ровно тот случай, для которого браузер запрос разрешает.
   * Опасен был бы автоматический запрос без действия человека.
   */
  const toggleCamera = useCallback(() => {
    const lk = livekitRef.current;
    if (!lk) return;
    // Пока выбирается/показывается экран, камера управляется только
    // screen-share переходом. Иначе пользователь мог бы включить второй
    // исходящий video source поверх уже опубликованного экрана.
    if (screenBusyRef.current || screenTrackRef.current) return;
    const track = camTrackRef.current;

    // Путь 1 — трек есть: простой mute/unmute, как было.
    if (track) {
      const enable = !cameraOn;
      // setCameraEnabled на localParticipant — канонический путь LiveKit;
      // при выключенной камере SFU перестаёт пересылать кадры, у других
      // приходит TrackUnsubscribed и тайл убирается сам.
      void lk.localParticipant.setCameraEnabled(enable).then(() => {
        diagnostics.add("local-track-state", { kind: "video", source: "camera", muted: !enable });
        if (enable && livekitRef.current === lk) {
          // switchActiveDevice на muted-камере откладывает реальный restart
          // до unmute. После него обновляем raw refs и локальный MediaStream,
          // иначе превью продолжит держать уже остановленную дорожку.
          const restarted = lk.localParticipant.getTrackPublication(Track.Source.Camera)?.track?.mediaStreamTrack;
          if (restarted && restarted !== camTrackRef.current) {
            camTrackRef.current = restarted;
            camStreamRef.current = new MediaStream([restarted]);
            setVideoTiles((prev) => prev.map((tile) =>
              tile.key === "cam:local"
                ? { ...tile, stream: new MediaStream([restarted]) }
                : tile,
            ));
            diagnostics.add("device-switch", { kind: "camera", detail: "replacement-success" });
          }
        }
      }).catch((e) => {
        diagnostics.add("local-track-state-error", { kind: "video", source: "camera", error: e });
        console.error(`[call] setCameraEnabled(${enable}) failed:`, e);
      });
      cameraOnRef.current = enable;
      setCameraOn(enable);
      return;
    }

    // Путь 2 — трека нет: добавляем видео в идущий аудиозвонок.
    // Повторное нажатие, пока висит системный запрос доступа,
    // дало бы два getUserMedia и два трека — отсюда флаг cameraBusy.
    if (cameraBusyRef.current) return;
    cameraBusyRef.current = true;
    setCameraBusy(true);
    setCameraError(null);

    void (async () => {
      try {
        const camStream = await navigator.mediaDevices.getUserMedia({
          video: deviceConstraint("video") ?? true,
        });
        setDevicesPermissionBump((n) => n + 1);
        // За время системного запроса человек мог выйти из звонка:
        // тогда livekitRef уже другой (или null) — гасим захваченное
        // устройство, иначе индикатор камеры останется гореть.
        if (livekitRef.current !== lk) {
          camStream.getTracks().forEach((t) => t.stop());
          return;
        }
        const camTrack = camStream.getVideoTracks()[0] ?? null;
        if (!camTrack) throw new Error(tr("ui.kamera_ne_dala_videodorozhku"));

        camStreamRef.current = camStream;
        camTrackRef.current = camTrack;

        // Тот же порядок публикации, что в join: Source.Camera, чтобы
        // SFU отличал камеру от демонстрации экрана.
        await lk.localParticipant.publishTrack(camTrack, { source: Track.Source.Camera });
        diagnostics.add("local-track-published", { kind: camTrack.kind, source: "camera", muted: false });
        if (livekitRef.current !== lk) return; // вышли, пока публиковали

        // Своё превью в раскладку — как в join. Защита от дубля по ключу
        // "cam:local": если плитка почему-то уже есть, заменяем, а не кладём вторую.
        setVideoTiles((prev) => [
          {
            key: "cam:local",
            stream: new MediaStream([camTrack]),
            local: true,
            label: tr("timeline.senderMe"),
            participantIdentity: lk.localParticipant.identity,
            speaking: false,
            kind: "camera" as const,
          },
          ...prev.filter((t) => t.key !== "cam:local"),
        ]);
        cameraOnRef.current = true;
        setCameraOn(true);
        setCameraInitialized(true);
        // Звонок стал видео: экран и плашки ориентируются на isVideo.
        isVideoRef.current = true;
        setIsVideo(true);
      } catch (e) {
        diagnostics.add("local-track-publish-error", { kind: "video", source: "camera", error: e });
        // Отказ в доступе / камера занята — говорим текстом, не молчим.
        // Звонок НЕ рвём: аудио продолжается, не вышло только видео.
        const msg = e instanceof Error ? e.message : String(e);
        console.error("[call] add camera to ongoing call failed:", e);
        setCameraError(msg);
        cameraOnRef.current = false;
        setCameraOn(false);
        const s = camStreamRef.current;
        camStreamRef.current = null;
        camTrackRef.current = null;
        if (s) s.getTracks().forEach((t) => t.stop());
      } finally {
        cameraBusyRef.current = false;
        setCameraBusy(false);
      }
    })();
  }, [cameraOn, deviceConstraint, diagnostics]);

  /** Тумблер микрофона вкл/выкл (STEP6: два тумблера внутри звонка). */
  const toggleMic = useCallback(() => {
    const lk = livekitRef.current;
    if (!lk) return;
    const enable = !micOn;
    void lk.localParticipant.setMicrophoneEnabled(enable).then(() => {
      diagnostics.add("local-track-state", { kind: "audio", source: "microphone", muted: !enable });
      if (!enable) {
        setActiveSpeakerIdentity((current) =>
          current === lk.localParticipant.identity ? null : current,
        );
      }
      if (enable && livekitRef.current === lk) {
        const restarted = lk.localParticipant.getTrackPublication(Track.Source.Microphone)?.track?.mediaStreamTrack;
        if (restarted && micStreamRef.current?.getAudioTracks()[0] !== restarted) {
          micStreamRef.current = new MediaStream([restarted]);
          diagnostics.add("device-switch", { kind: "mic", detail: "replacement-success" });
        }
      }
    }).catch((e) => {
      diagnostics.add("local-track-state-error", { kind: "audio", source: "microphone", error: e });
      console.error(`[call] setMicrophoneEnabled(${enable}) failed:`, e);
    });
    setMicOn(enable);
  }, [micOn, diagnostics]);

  /**
   * Единый путь остановки для нашей кнопки, browser/system Stop Sharing,
   * ошибок capture/publish и гонок cleanup. Ref очищается ДО await, поэтому
   * повторный onended/клик идемпотентен и не добавляет локальный экран снова.
   * Камера восстанавливается только если была on до screen share и именно
   * этот переход успел её выключить.
   */
  const stopScreenShare = useCallback(async () => {
    const transitionAlreadyBusy = screenBusyRef.current;
    if (!transitionAlreadyBusy) {
      screenBusyRef.current = true;
      setScreenBusy(true);
    }
    const lk = livekitRef.current;
    const track = screenTrackRef.current;
    const stream = screenStreamRef.current;
    const restoreCamera =
      cameraWasOnBeforeScreenRef.current && cameraDisabledForScreenRef.current;

    screenTrackRef.current = null;
    screenStreamRef.current = null;
    cameraWasOnBeforeScreenRef.current = false;
    cameraDisabledForScreenRef.current = false;
    setScreenTrack(null);
    setVideoTiles((prev) => prev.filter((t) => t.key !== "screen:local"));

    if (track) track.onended = null;
    if (lk && track) {
      try {
        await lk.localParticipant.unpublishTrack(track);
        diagnostics.add("local-track-unpublished", {
          kind: track.kind,
          source: "screen",
          muted: !track.enabled,
        });
      } catch (e) {
        diagnostics.add("local-track-unpublish-error", {
          kind: track.kind,
          source: "screen",
          error: e,
        });
        console.error("[call] unpublish screen track failed:", e);
      }
    }
    if (stream) stream.getTracks().forEach((t) => t.stop());
    else if (track) track.stop();

    if (restoreCamera && lk && livekitRef.current === lk) {
      try {
        await lk.localParticipant.setCameraEnabled(true);
        if (livekitRef.current === lk) {
          const restarted = lk.localParticipant.getTrackPublication(Track.Source.Camera)?.track?.mediaStreamTrack;
          if (restarted) {
            camTrackRef.current = restarted;
            camStreamRef.current = new MediaStream([restarted]);
            setVideoTiles((prev) => [
              {
                key: "cam:local",
                stream: new MediaStream([restarted]),
                local: true,
                label: tr("timeline.senderMe"),
                participantIdentity: lk.localParticipant.identity,
                speaking: false,
                kind: "camera" as const,
              },
              ...prev.filter((tile) => tile.key !== "cam:local"),
            ]);
          }
          cameraOnRef.current = true;
          setCameraOn(true);
          diagnostics.add("local-track-state", {
            kind: "video",
            source: "camera",
            muted: false,
            detail: "restored-after-screen",
          });
        }
      } catch (e) {
        cameraOnRef.current = false;
        setCameraOn(false);
        setCameraError(e instanceof Error ? e.message : String(e));
        diagnostics.add("local-track-state-error", {
          kind: "video",
          source: "camera",
          detail: "restore-after-screen",
          error: e,
        });
      }
    }
    if (!transitionAlreadyBusy) {
      screenBusyRef.current = false;
      setScreenBusy(false);
    }
  }, [diagnostics]);

  /**
   * Screen share заменяет исходящую камеру на время публикации: сначала
   * камера mute, затем publish экрана; при любом завершении — обратный
   * переход только для камеры, которая была включена до запуска.
   */
  const toggleScreenShare = useCallback(() => {
    const lk = livekitRef.current;
    if (!lk) return;

    if (screenTrackRef.current) {
      void stopScreenShare();
      return;
    }
    if (screenBusyRef.current) return;

    screenBusyRef.current = true;
    setScreenBusy(true);
    setScreenError(null);
    const cameraPublication = lk.localParticipant.getTrackPublication(Track.Source.Camera);
    cameraWasOnBeforeScreenRef.current = Boolean(
      cameraPublication?.track && !cameraPublication.isMuted,
    );
    cameraDisabledForScreenRef.current = false;

    void (async () => {
      let stream: MediaStream | null = null;
      try {
        stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        if (livekitRef.current !== lk) {
          stream.getTracks().forEach((t) => t.stop());
          cameraWasOnBeforeScreenRef.current = false;
          return;
        }
        const track = stream.getVideoTracks()[0] ?? null;
        if (!track) throw new Error(tr("ui.brauzer_ne_dal_videodorozhku_ekrana"));

        screenStreamRef.current = stream;
        screenTrackRef.current = track;
        track.onended = () => {
          debugLogL(`[call] screen share ended via browser UI room=${room.roomId}`);
          void stopScreenShare();
        };

        // Only one outgoing video source: camera is fully muted before the
        // screen publication is allowed to begin.
        if (cameraWasOnBeforeScreenRef.current) {
          // Keep the pre-share fact locally: onended may clear the shared
          // refs while WebKit is still resolving setCameraEnabled(false).
          const restoreAfterStop = cameraWasOnBeforeScreenRef.current;
          await lk.localParticipant.setCameraEnabled(false);
          cameraDisabledForScreenRef.current = true;
          if (livekitRef.current !== lk || screenTrackRef.current !== track) {
            cameraWasOnBeforeScreenRef.current = restoreAfterStop;
            await stopScreenShare();
            return;
          }
          cameraOnRef.current = false;
          setCameraOn(false);
          diagnostics.add("local-track-state", {
            kind: "video",
            source: "camera",
            muted: true,
            detail: "disabled-for-screen",
          });
        }

        await lk.localParticipant.publishTrack(track, {
          source: Track.Source.ScreenShare,
        });
        diagnostics.add("local-track-published", {
          kind: track.kind,
          source: "screen",
          muted: !track.enabled,
        });
        if (livekitRef.current !== lk || screenTrackRef.current !== track) return;

        setScreenTrack(track);
        setVideoTiles((prev) => [
          {
            key: "screen:local",
            stream: new MediaStream([track]),
            local: true,
            label: tr("ui.ekran_vy"),
            participantIdentity: lk.localParticipant.identity,
            speaking: false,
            kind: "screen" as const,
          },
          // Camera is muted while sharing and must not remain as a frozen
          // second local preview. stopScreenShare recreates it only when the
          // camera really was enabled before sharing.
          ...prev.filter((t) => t.key !== "screen:local" && t.key !== "cam:local"),
        ]);
        debugLogL(`[call] screen share started room=${room.roomId}`);
      } catch (e) {
        diagnostics.add("local-track-publish-error", {
          kind: "video",
          source: "screen",
          error: e,
        });
        const msg = e instanceof Error ? e.message : String(e);
        console.error("[call] screen share failed:", e);
        setScreenError(
          /Permission denied|NotAllowedError|dismissed|cancel/i.test(msg)
            ? tr("ui.demonstraciya_ekrana_ne_nachalas_dostup_n")
            : msg,
        );
        await stopScreenShare();
      } finally {
        screenBusyRef.current = false;
        setScreenBusy(false);
      }
    })();
  }, [room.roomId, stopScreenShare, diagnostics]);

  // Слушатель отказа: ставим после leave, он им пользуется.
  useEffect(() => {
    if (state !== "in-call") return;
    const onDecline = (
      ev: MatrixEvent,
      r: Room | undefined,
      toStartOfTimeline?: boolean,
    ) => {
      if (!r || r.roomId !== room.roomId) return;
      if (ev.getType() !== "org.matrix.msc4310.rtc.decline") return;
      if (toStartOfTimeline) return;                     // историческое событие
      if (Date.now() - ev.getTs() > 60_000) return;      // не от этого звонка
      const sender = ev.getSender();
      if (!sender || sender === client.getUserId()) return; // свой же отказ
      const name = r.getMember(sender)?.name ?? sender;
      console.info(`[call] declined by ${sender} room=${room.roomId}`);
      setDeclinedBy(name);
      // ЗАВЕРШАЕМ ТОЛЬКО ЕСЛИ Я ОДИН И ЖДУ ОТВЕТА.
      //
      // Иначе в группе получается дефект: A звонит, B и C уже разговаривают,
      // B отклоняет входящий со своего второго устройства — и C, который
      // просто сидит in-call, вылетает из живого разговора. Отказ адресован
      // тому, кто ЗВОНИТ и ждёт, а не всем присутствующим.
      //
      // participants считается по людям (см. recompute), поэтому <= 1
      // означает именно «я один», а не «я с двух устройств».
      if (participants <= 1) leave();
    };
    client.on(MatrixRoomEvent.Timeline, onDecline);
    return () => {
      client.off(MatrixRoomEvent.Timeline, onDecline);
    };
  }, [client, room, state, leave, participants]);

  /**
   * ЗАВЕРШЕНИЕ ЗВОНКА БЕЗ УЧАСТНИКОВ (Антон 14:23 и 15:27).
   * ПОДТВЕРЖДЕНО ЖИВЬЁМ 15:41: «звонок идёт, сброс работает».
   *
   * Зачем: членство в звонке живёт 4 часа (замер стенда: expires=14400000),
   * поэтому оставшийся один человек висел «в звонке» часами — «иначе будет
   * висеть как в Slack, это нерационально».
   *
   * Правила:
   *   • живых людей >= 2 -> НЕ трогаем вообще, сколько бы звонок ни шёл;
   *   • разговор шёл и собеседник вышел -> конец НЕМЕДЛЕННО, без отсчёта
   *     («если 2 участника и один повесил трубку — закончиться немедленно»);
   *   • я позвонил и никто не ответил -> 60 с с видимым отсчётом, потом выход.
   *     Немедленный выход здесь был бы абсурдом: звонок обрывался бы в момент
   *     нажатия кнопки.
   *
   * Роли инициатора в MatrixRTC нет — правило одинаково для всех и закрывает
   * сразу «все вышли», «инициатор вышел», «никто не ответил».
   */
  const SOLO_TIMEOUT_MS = 60_000;
  const [soloSecondsLeft, setSoloSecondsLeft] = useState<number | null>(null);

  /**
   * Был ли в звонке кто-то кроме меня (Антон 15:27).
   *
   * Разводит ДВА разных случая «я один в звонке»:
   *   • разговор шёл и собеседник вышел -> завершаем НЕМЕДЛЕННО,
   *     продолжать не с кем (Антон 15:27: «звонок должен закончиться
   *     немедленно»). Отсчёт тут не показываем вообще.
   *   • я позвонил и никто ещё не ответил -> ждём 60 с (lifetime
   *     уведомления). Немедленный выход здесь был бы абсурдом:
   *     звонок завершался бы в момент нажатия кнопки.
   *
   * Сбрасывается при выходе из звонка (state ушёл из in-call).
   */
  const hadCompanyRef = useRef(false);
  useEffect(() => {
    if (state !== "in-call") {
      hadCompanyRef.current = false;
      return;
    }
    if (participants > 1) hadCompanyRef.current = true;
  }, [state, participants]);

  useEffect(() => {
    if (state !== "in-call" || participants > 1) {
      setSoloSecondsLeft(null);
      return;
    }
    // Разговор шёл и собеседник вышел — конец сразу, без отсчёта.
    if (hadCompanyRef.current) {
      debugLog(`[call] peer left room=${room.roomId}, ending immediately`);
      setSoloSecondsLeft(null);
      leave();
      return;
    }
    const deadline = Date.now() + SOLO_TIMEOUT_MS;
    setSoloSecondsLeft(Math.ceil(SOLO_TIMEOUT_MS / 1000));
    const tick = window.setInterval(() => {
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setSoloSecondsLeft(left);
      if (left === 0) {
        debugLog(`[call] nobody answered room=${room.roomId}, auto-leave`);
        window.clearInterval(tick);
        leave();
      }
    }, 1000);
    return () => {
      window.clearInterval(tick);
      setSoloSecondsLeft(null);
    };
  }, [state, participants, leave, room.roomId]);

  // Гудок исходящего вызова: звучит ровно пока я один после того, как
  // позвонил (soloSecondsLeft !== null); гаснет при ответе/отказе/выходе.
  // (Антон 2026-09-17 14:40: «нет никакого гудка, непонятно идёт вызов или нет»)
  useEffect(() => {
    if (soloSecondsLeft !== null) startDialTone();
    else stopDialTone();
    return () => stopDialTone();
  }, [soloSecondsLeft !== null]);

  return {
    roomEncrypted,
    state,
    error,
    joined: state === "in-call",
    participants,
    join,
    leave,
    soloSecondsLeft,
    declinedBy,
    isVideo,
    cameraOn,
    toggleCamera,
    cameraBusy,
    cameraError,
    cameraInitialized,
    micOn,
    toggleMic,
    screenOn,
    screenBusy,
    screenError,
    toggleScreenShare,
    videoTiles: videoTiles.map((tile) => ({
      ...tile,
      speaking: tile.participantIdentity === activeSpeakerIdentity,
      micMuted: isMicMuted(livekitRef.current, tile.participantIdentity),
      cameraOff:
        tile.kind !== "screen" && isCameraMuted(livekitRef.current, tile.participantIdentity),
    })),
    callParticipants: callParticipantIdentities.map((identity) => {
      // LiveKit participant identity is NOT a Matrix user ID. For modern
      // MatrixRTC memberships it is an anonymised rtcBackendIdentity hash;
      // for legacy memberships it can contain user+device. Looking it up via
      // room.getMember(identity) therefore returned no member and no avatar.
      // The SDK has already parsed the authoritative identity mapping for us.
      const membership = session.memberships.find(
        (candidate) => !candidate.isExpired() && candidate.rtcBackendIdentity === identity,
      );
      const local = identity === livekitRef.current?.localParticipant.identity;
      // Legacy identities look like "@user:server:DEVICEID". When the SDK has
      // no matching membership (expired or not yet synced) we still know the
      // Matrix user from the identity itself, so strip the device suffix
      // instead of showing the raw identity as the participant's name
      // (Anton 2026-09-18 11:35 UTC: "@vs:algsuccess.com:M2QEzM…" on a tile).
      const userId =
        membership?.userId ??
        (local ? client.getUserId() : null) ??
        userIdFromLegacyIdentity(identity);
      const member = userId ? room.getMember(userId) : null;
      return {
        identity,
        label: local ? tr("timeline.senderMe") : member?.name ?? userId ?? identity,
        avatarMxc: member?.getMxcAvatarUrl() ?? null,
        local,
        speaking: identity === activeSpeakerIdentity,
        micMuted: isMicMuted(livekitRef.current, identity),
        // Только в видеозвонке: в аудиозвонке перечёркнутая камера у всех — шум.
        cameraOff: isVideo && isCameraMuted(livekitRef.current, identity),
      };
    }),
    devices,
    activeDevices,
    selectDevice,
    deviceNotice,
    dismissDeviceNotice,
    diagnosticsAvailable: diagnostics.hasEntries,
    copyDiagnostics,
  };
}
